"use client";
import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import React from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import Papa from "papaparse";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import AlertDialog from "./AlertDialog";
import { FiTrash2 } from "react-icons/fi";
import { createPortal } from "react-dom";
import VarianceModal from "./BudgetVarianceModal2";
import { TrashIcon } from "@heroicons/react/20/solid";
import zIndex from "@mui/material/styles/zIndex";
import AssemblyBudgetView from "./AssemblyBudgetView";

// Custom styles for react-select
const customStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: "#374151",
    borderColor: state.isFocused ? "#f97316" : "#4b5563",
    color: "#e5e7eb",
    boxShadow: "none",
    zIndex: 0,
    outline: "none",
    "&:hover": { borderColor: "#f97316" },
    minHeight: "32px",
    height: "32px",
    fontSize: "12px",
  }),
  input: (provided) => ({
    ...provided,
    color: "#e5e7eb",
    outline: "none",
    boxShadow: "none",
    border: "none",
    margin: 0,

    padding: 0,
    "&:focus": {
      outline: "none",
      boxShadow: "none",
      border: "none",
    },
  }),
  singleValue: (provided) => ({
    ...provided,

    color: "#e5e7eb",
    fontSize: "12px",
  }),
  placeholder: (provided) => ({
    ...provided,
    color: "#9ca3af",
    fontSize: "12px",
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? "#4b5563" : "#374151",
    color: state.isSelected ? "#f97316" : "#e5e7eb",
    "&:hover": { backgroundColor: "#4b5563" },
    zIndex: 0,
    fontSize: "12px",
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: "#374151",
    borderRadius: "0.375rem",
    zIndex: 0,
    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: "0 4px",
  }),
  indicatorsContainer: (provided) => ({
    ...provided,
    padding: "0",
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    padding: "2px",
    zIndex: 0,
  }),
  multiValue: (provided) => ({
    ...provided,
    backgroundColor: "#4b5563",
    zIndex: 0,
    borderRadius: "4px",
  }),
  multiValueLabel: (provided) => ({
    ...provided,
    color: "#e5e7eb",
    fontSize: "11px",
    zIndex: 0,
    padding: "2px 4px",
  }),
  multiValueRemove: (provided) => ({
    ...provided,
    zIndex: 0,
    color: "#9ca3af",
    ":hover": {
      backgroundColor: "#f97316",
      color: "white",
    },
  }),
};

// =====================================================
// HELPER FUNCTIONS FOR V2 PARSING
// =====================================================
const cleanNumber = (value) => {
  if (value == null || value === "-" || value === "") return 0;
  const str = String(value).trim();
  const cleaned = str.replace(/[$,]/g, "").replace(/[^0-9.-]/g, "");
  return parseFloat(cleaned) || 0;
};

const parseUnitCost = (value) => {
  if (value == null || value === "-" || value === "") return 0;
  const str = String(value).trim();
  const match = str.match(/^[\d.,]+/);
  if (match) {
    return parseFloat(match[0].replace(/,/g, "")) || 0;
  }
  return 0;
};

const parseReqQty = (value) => {
  if (value == null || value === "-" || value === "") return 0;
  const num = parseFloat(String(value).replace(/[^0-9.-]/g, ""));
  return isNaN(num) ? 0 : Math.round(num);
};

function normalise(str) {
  if (!str) return "";
  return String(str)
    .toLowerCase()
    .replace(/[''"""]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function buildItemSearchBlob(item) {
  const parts = [
    item.conditionName,
    item.sectionNumber,
    item.category,
    item.itemDescription,
    item.costCodeDescription,
    item.size,
    item.latestSize,
    item.uom,
    item.container,
    item.bidArea,
    item.materialCode,
    item.laborCode,
    item.note,
    typeof item.vendor === "object" ? item.vendor?.companyName || "" : "",
  ];
  return normalise(parts.join("  "));
}

function tokenVariants(token) {
  const variants = [token];
  const fractionCondensed = token.match(/^(\d+)(\d)(\d)$/);
  if (fractionCondensed) {
    const [, a, b, c] = fractionCondensed;
    variants.push(`${a}-${b}/${c}`, `${a} ${b}/${c}`, `${a}${b}/${c}`);
  }
  const fractionExpanded = token.match(/^(\d+)[\s-]?(\d)\/(\d)$/);
  if (fractionExpanded) {
    const [, a, b, c] = fractionExpanded;
    variants.push(`${a}${b}${c}`);
  }
  const gaugeMatch = token.match(/^(\d+)(ga|gauge)$/i);
  if (gaugeMatch) {
    const [, num] = gaugeMatch;
    variants.push(`${num}ga`, `${num} ga`, `${num}gauge`);
  }
  if (token === "drywall") variants.push("dry wall");
  if (token === "dry") variants.push("drywall");
  return variants;
}

function multiTokenSearchItems(items, searchStr) {
  const raw = normalise(searchStr);
  if (!raw) return items;
  const tokens = raw.split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return items;
  return items.filter((item) => {
    const blob = item._searchBlob || buildItemSearchBlob(item);
    if (!item._searchBlob) item._searchBlob = blob;
    return tokens.every((token) =>
      tokenVariants(token).some((v) => blob.includes(v)),
    );
  });
}

export default function BudgetTab({ project, setProject, onGeneratePO }) {
  const navigate = useNavigate();
  const [addItemsBudgetId, setAddItemsBudgetId] = useState(null);
  const [addItemsFile, setAddItemsFile] = useState(null);
  const [showBudgetForm, setShowBudgetForm] = useState(false);
  const [budgetTitle, setBudgetTitle] = useState("");
  const [viewMode, setViewMode] = useState("single"); // "single" | "assembly"
  const [budgetFile, setBudgetFile] = useState(null);
  const [vendors, setVendors] = useState([]);
  const budgets = project.budgets || [];
  const [netItems, setNetItems] = useState([]);
  const [loadingNet, setLoadingNet] = useState(true);
  const [expandedBudget, setExpandedBudget] = useState(null);
  const [editingBudget, setEditingBudget] = useState(null);
  const [editedItems, setEditedItems] = useState([]);
  const allChangesRef = useRef([]);
  const [conditionSearchText, setConditionSearchText] = useState("");
  const [budgetTotalsCache, setBudgetTotalsCache] = useState({});
  const [assemblyViewItems, setAssemblyViewItems] = useState([]);
  const [loadingAssemblyView, setLoadingAssemblyView] = useState(false);
  const [summaryTotals, setSummaryTotals] = useState({
    totalEstimatedCost: 0,
    originalEstimatedCost: 0,
    originalBaseCost: 0, // NEW
    changeOrderImpact: 0,
    tAndMCost: 0,
    varianceImpact: 0,
    isLoading: true,
  });

  const [showVarianceModal, setShowVarianceModal] = useState(false);
  const [selectedLineItems, setSelectedLineItems] = useState([]);
  const [selectedBudgets, setSelectedBudgets] = useState([]);
  const [showBulkVendor, setShowBulkVendor] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isUpdatingBudget, setIsUpdatingBudget] = useState(false);
  const [expandedItemId, setExpandedItemId] = useState(null); // for variance history expand
  const [isMounted, setIsMounted] = useState(false);

  const API_BASE = "https://erp-production-72da01c8e651.herokuapp.com/api";
  const [sourceFilter, setSourceFilter] = useState([]); // 'budget' | 'variance' | 'change_order'
  const [dialog, setDialog] = useState({
    open: false,
    type: "error",
    title: "",
    message: "",
    onConfirm: () => {},
  });
  const [isAddingToVariance, setIsAddingToVariance] = useState(false);
  const [selectedVarianceId, setSelectedVarianceId] = useState(null);

  const [budgetDetailItems, setBudgetDetailItems] = useState([]);
  const [loadingBudgetDetail, setLoadingBudgetDetail] = useState(false);

  // Updated filters state with condition filter
  const [filters, setFilters] = useState({
    bidArea: [], // was: null
    vendor: [], // was: null
    category: [], // was: null
    section: [], // was: null
    descriptionSearch: "", // keep as string
    uom: [], // was: null
    size: [], // was: null
    poWo: [], // was: null
    condition: [], // was: null
  });

  const [showFilter, setShowFilter] = useState(null);

  // PERFORMANCE: Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 500;

  // PERFORMANCE: Debounced search state
  const [searchInput, setSearchInput] = useState("");
  const searchTimeoutRef = useRef(null);

  // Refs for filter inputs
  const uomRef = useRef(null);
  const sizeRef = useRef(null);
  const sectionRef = useRef(null);
  const categoryRef = useRef(null);
  const vendorRef = useRef(null);
  const bidAreaRef = useRef(null);
  const descriptionRef = useRef(null);
  const poWoRef = useRef(null);
  const conditionRef = useRef(null); // NEW: condition ref

  // Handle click outside to close filter dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!showFilter) return;

      const filterRefs = {
        category: categoryRef,
        section: sectionRef,
        condition: conditionRef,
        bidArea: bidAreaRef,
        descriptionSearch: descriptionRef,
        vendor: vendorRef,
        uom: uomRef,
        size: sizeRef,
        poWo: poWoRef,
      };

      const activeRef = filterRefs[showFilter];

      // Check if click is outside the active filter dropdown
      if (activeRef?.current && !activeRef.current.contains(event.target)) {
        // Also check if click is inside a portal (react-select menu)
        const isInsidePortal =
          event.target.closest(".css-26l3qy-menu") ||
          event.target.closest('[class*="-menu"]') ||
          event.target.closest('[id^="react-select"]');

        if (!isInsidePortal) {
          setShowFilter(null);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showFilter]);

  useEffect(() => {
    const fetchVendors = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(
          "https://erp-production-72da01c8e651.herokuapp.com/api/accounts/vendors",
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const vendorData = response.data.filter((v) => v.type === "vendor");
        setVendors(vendorData);
      } catch (err) {
        setDialog({
          open: true,
          type: "error",
          title: "Error",
          message:
            "Failed to fetch vendors: " +
            (err.response?.data?.message || err.message),
          onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
        });
      }
    };
    fetchVendors();
  }, []);

  useEffect(() => {
    if (!project?._id) return;

    const fetchSummaryTotals = async () => {
      setSummaryTotals((prev) => ({ ...prev, isLoading: true }));
      try {
        const token = localStorage.getItem("token");
        // NEW: Lightweight endpoint that returns ONLY totals
        const response = await axios.get(
          `${API_BASE}/net-assemblies/project-summary/${project._id}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );

        if (response.data) {
          setSummaryTotals({
            totalEstimatedCost: response.data.totalEstimatedCost || 0,
            originalEstimatedCost: response.data.originalEstimatedCost || 0,
            originalBaseCost: response.data.originalBaseCost || 0, // NEW
            changeOrderImpact: response.data.changeOrderImpact || 0,
            tAndMCost: response.data.tAndMCost || 0,
            varianceImpact: response.data.varianceImpact || 0,
            isLoading: false,
          });

          if (response.data.budgetTotals) {
            setBudgetTotalsCache(response.data.budgetTotals);
          }
        }
      } catch (err) {
        console.error("Failed to fetch summary totals:", err);
        setSummaryTotals((prev) => ({ ...prev, isLoading: false }));
      }
    };

    fetchSummaryTotals();
  }, [project?._id]);

  useEffect(() => {
    if (!project?._id || !expandedBudget) {
      setBudgetDetailItems([]);
      return;
    }

    const budget = project?.budgets?.find((b) => b._id === expandedBudget);
    if (!budget) return;

    if (editingBudget === expandedBudget && budget.status !== "confirmed") {
      return;
    }

    const fetchBudgetDetail = async () => {
      setLoadingBudgetDetail(true);
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(
          `${API_BASE}/net-assemblies/budget-detail/${project._id}/${expandedBudget}`,
          {
            headers: { Authorization: `Bearer ${token}` },
            params: { category: "all", includeClosed: "false" },
          },
        );

        if (response.data) {
          setBudgetDetailItems(response.data.items || []);

          // UPDATE: Cache the total from the response
          if (response.data.totals) {
            setBudgetTotalsCache((prev) => ({
              ...prev,
              [expandedBudget]: response.data.totals.totalAmount,
            }));
          }
        }
      } catch (err) {
        console.error("Failed to fetch budget detail:", err);
        setBudgetDetailItems([]);
      } finally {
        setLoadingBudgetDetail(false);
      }
    };

    fetchBudgetDetail();
  }, [project?._id, expandedBudget, editingBudget]);

  const getDisplayUom = (item) => {
    if (!item || item.category !== "M")
      return item?.uom || item?.container || "—";
    const uom = (item.uom || item.container || "").trim().toUpperCase();
    const coverageBased = ["SF", "LF"];
    const isCoverageBased = coverageBased.some((u) => uom.includes(u));
    return isCoverageBased ? "PC" : uom || "—";
  };

  const getDisplayPackageCost = (item) => {
    if (!item || item.category !== "M") return getDisplayPrice(item);
    const uom = (item.uom || item.container || "").trim().toUpperCase();
    const coverageBased = ["SF", "LF"];
    const isCoverageBased = coverageBased.some((u) => uom.includes(u));
    if (!isCoverageBased) return getDisplayPrice(item);
    const coverage =
      Number(parseFloat(item.coveragePerUnit)) ||
      calculateCoverage(item.size, item.uom || item.container) ||
      1;
    return getDisplayPrice(item) * coverage;
  };

  useEffect(() => {
    if (!project?._id || viewMode !== "assembly") return;

    const fetchAssemblyViewItems = async () => {
      setLoadingAssemblyView(true);
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(
          `${API_BASE}/net-assemblies/project-by-source/${project._id}`,
          {
            headers: { Authorization: `Bearer ${token}` },
            params: {
              category: "all",
              mode: "combined",
              includeClosed: "false",
            },
          },
        );

        if (response.data) {
          let items = response.data.combined || [];
          if (items.length === 0) {
            const originals = response.data.originalItems || [];
            const cos = response.data.changeOrderItems || [];
            items = [...originals, ...cos];
            const varianceItems = response.data.varianceItems || {};
            Object.values(varianceItems).forEach((vItems) => {
              if (Array.isArray(vItems)) items.push(...vItems);
            });
          }
          setAssemblyViewItems(items);
        }
      } catch (err) {
        console.error("Failed to fetch assembly view items:", err);
        setAssemblyViewItems([]);
      } finally {
        setLoadingAssemblyView(false);
      }
    };

    fetchAssemblyViewItems();
  }, [project?._id, viewMode]);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Helper to generate unique selection key for each item
  const getItemSelectionKey = useCallback((item) => {
    if (!item) return null;
    const base = item._id || item.key || "";
    const suffix = item.isHistoricalPriceLine
      ? "_hist"
      : item.isPriceChangeRow
        ? "_newprice"
        : item.isNewSpec
          ? "_newspec"
          : "";
    return `${base}${suffix}`;
  }, []);

  // PERFORMANCE: Reset page when filters or expanded budget changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, expandedBudget]);

  // PERFORMANCE: Memoized filter change handler
  const handleFilterChange = useCallback((filterType, value) => {
    if (filterType === "descriptionSearch") {
      setFilters((prev) => ({ ...prev, descriptionSearch: value }));
    } else {
      setFilters((prev) => ({
        ...prev,
        [filterType]: value || [],
      }));
    }
  }, []);
  // PERFORMANCE: Debounced description search handler
  const handleDescriptionSearchChange = useCallback((e) => {
    const value = e.target.value;
    setSearchInput(value);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      setFilters((prev) => ({ ...prev, descriptionSearch: value }));
    }, 250); // faster for multi-token feel
  }, []);
  // PERFORMANCE: Memoized clearAllFilters
  const clearAllFilters = useCallback(() => {
    setFilters({
      bidArea: [],
      vendor: [],
      category: [],
      section: [],
      descriptionSearch: "",
      uom: [],
      size: [],
      poWo: [],
      condition: [],
    });
    setSearchInput("");
    setConditionSearchText(""); // <-- add this
    setSelectedLineItems([]);
    setShowFilter(null);
    setCurrentPage(1);
    setShowBulkVendor(false);
  }, []);

  const toggleFilter = (field) => {
    setShowFilter((prev) => (prev === field ? null : field));
  };

  // =====================================================
  // FILE UPLOAD HANDLER (V2 with auto-detection)
  // =====================================================
  const handleFileUpload = () => {
    if (!budgetFile || !budgetTitle.trim()) {
      setDialog({
        open: true,
        type: "error",
        title: "Error",
        message: "Please provide a title and select a file.",
        onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
      });
      return;
    }
    const fileExtension = budgetFile.name.split(".").pop().toLowerCase();
    const processFile = async (data) => {
      // Check if this is new format (has "Condition" column) or old format
      const isNewFormat = data.length > 0 && "Condition" in data[0];
      if (isNewFormat) {
        // V2 FORMAT PROCESSING
        // === V2 FORMAT PROCESSING ===
        const filteredData = data.filter((row) =>
          ["M", "L"].includes(row["Category"]),
        );

        if (filteredData.length === 0) {
          setDialog({
            open: true,
            type: "error",
            title: "Error",
            message:
              "No valid material (M) or labor (L) items found in the uploaded file.",
            onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
          });
          return;
        }

        // Helper functions remain the same
        const extractSize = (sizeStr) => {
          if (!sizeStr) return "";
          const str = String(sizeStr).trim();
          const parenMatch = str.match(/\(([^)]+)\)/);
          if (parenMatch) {
            return parenMatch[1].trim();
          }
          return str;
        };

        // First: Get the current maximum itemNumber in the entire project
        let maxItemNumber = 0;

        project.budgets.forEach((budget) => {
          if (budget.lineItems && Array.isArray(budget.lineItems)) {
            budget.lineItems.forEach((item) => {
              if (item.itemNumber && item.itemNumber > maxItemNumber) {
                maxItemNumber = item.itemNumber;
              }
            });
          }
        });

        // Now parse all rows
        const parsedItems = filteredData.map((row) => {
          const category = row["Category"];
          const unitCost = parseUnitCost(row["Unit Cost"]);
          const quantity = cleanNumber(row["Quantity"]);
          const reqQty = parseReqQty(row["Req Qty"]);
          const materialTotal = cleanNumber(row["Mat. Total"]);
          const laborTotal = cleanNumber(row["Labor Total"]);
          const itemTotal = cleanNumber(row["Item Total"]);
          const description = row["Description"] || "";
          const conditionName = (row["Condition"] || "").trim();
          const rawSize = row["Size"] || "";
          const parsedSize = extractSize(rawSize);

          let vendorId = null;
          if (category === "M") {
            const vendorName = row["Mat. Code"]?.trim() || "";
            if (vendorName && vendors.length > 0) {
              const matchedVendors = vendors.filter((v) =>
                v.companyName.toLowerCase().includes(vendorName.toLowerCase()),
              );
              if (matchedVendors.length >= 1) {
                vendorId = matchedVendors[0]._id;
              }
            }
          }

          // Assign next sequential project-wide itemNumber
          maxItemNumber += 1;

          return {
            itemNumber: maxItemNumber, // ← Unique across entire project
            conditionName: conditionName || "Uncategorized",
            laborId: row["Labor ID"] || "",
            materialCode: row["Mat. Code"] || "",
            laborCode: row["Labor Code"] || "",
            coveragePerUnit: String(row["Coverage/Unit"] || ""),
            orderAs: row["Order As"] || "",
            materialCostString: String(row["Material Cost"] || ""),
            materialTotal: materialTotal,
            laborTotal: laborTotal,
            itemTotal: itemTotal,
            sectionNumber: row["Section"] || "",
            category: category,
            costCodeDescription: description,
            itemDescription: description,
            description: description,
            amount: itemTotal || quantity * unitCost,
            bidArea: row["Area"] || "",
            area: row["Area"] || "",

            ...(category === "M" && {
              itemCode: row["Mat. Code"] || "",
              size: parsedSize,
              requiredQty: reqQty,
              container: row["UoM"] || "",
              price: unitCost,
              cost: unitCost,
              vendor: vendorId,
              uom: row["UoM"] || "",
              quantity: quantity,
            }),
            ...(category === "L" && {
              quantity: quantity,
              uom: row["UoM"] || "",
              laborHours: cleanNumber(row["Labor Hours"]) || 0,
              unitRate: unitCost,
              price: unitCost,
            }),
          };
        });

        // Now upload
        uploadBudgetV2(parsedItems);
      } else {
        // LEGACY FORMAT PROCESSING
        const parsedItems = data
          .filter((row) => ["M", "L"].includes(row["Category"]))
          .map((row) => {
            let vendorId = null;
            if (row["Category"] === "M" && row["Part Number"]?.trim()) {
              const vendorName = row["Part Number"].trim();
              const matchedVendors = vendors.filter((v) =>
                v.companyName.toLowerCase().includes(vendorName.toLowerCase()),
              );
              if (matchedVendors.length === 1) {
                vendorId = matchedVendors[0]._id;
              } else if (matchedVendors.length > 1) {
                vendorId = matchedVendors[0]._id;
              }
            }
            if (row["Category"] === "M" && row["Vendor"]?.trim()) {
              const vendorName = row["Vendor"].trim();
              const matchedVendors = vendors.filter((v) =>
                v.companyName.toLowerCase().includes(vendorName.toLowerCase()),
              );
              if (matchedVendors.length === 1) {
                vendorId = matchedVendors[0]._id;
              } else if (matchedVendors.length > 1) {
                vendorId = matchedVendors[0]._id;
              }
            }
            const unitPrice = cleanNumber(row[" Price "] || row["Price"]);
            const sellingPrice = cleanNumber(
              row["Customer Price"] || row[" Price "] || row["Price"],
            );
            const amount = cleanNumber(row[" Amount "] || row["Amount"]);
            let quantity;
            if (row["Category"] === "M") {
              quantity =
                amount > 0 && unitPrice > 0
                  ? amount / unitPrice <= 1
                    ? 1
                    : Math.round(amount / unitPrice)
                  : 0;
            } else {
              quantity = Math.round(cleanNumber(row["Quantity"])) || 0;
            }
            const unitRate =
              row["Category"] === "L" && quantity > 0
                ? Number((amount / quantity).toFixed(2))
                : 0;
            return {
              sectionNumber: row["Section #"] || "",
              category: row["Category"] || "",
              costCodeDescription: row["Cost Code Description"] || "",
              amount: Number(amount.toFixed(2)),
              vendor: vendorId,
              uom:
                row["Category"] === "M"
                  ? row["Container"] || row["UOM"] || "Sheet"
                  : row["UOM"] || "",
              ...(row["Category"] === "M" && {
                itemCode: row["Item Code"] || "",
                itemDescription: row["Item Description"] || "",
                size: row["Size"] || "",
                requiredQty: quantity,
                container: row["Container"] || "",
                bidArea: row["Bid Area"] || "",
                price: sellingPrice.toFixed(2),
                cost: unitPrice,
              }),
              ...(row["Category"] === "L" && {
                quantity: quantity,
                uom: row["UOM"] || "",
                laborHours: cleanNumber(row["Labor Hours"]) || 0,
                unitRate: unitRate,
                price: unitRate,
                bidArea: row["Bid Area"] || "",
              }),
            };
          });
        if (parsedItems.length === 0) {
          setDialog({
            open: true,
            type: "error",
            title: "Error",
            message:
              "No valid material (M) or labor (L) items found in the uploaded file.",
            onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
          });
          return;
        }
        uploadBudget(parsedItems);
      }
    };
    if (fileExtension === "csv") {
      Papa.parse(budgetFile, {
        header: true,
        skipEmptyLines: true,
        complete: (result) => processFile(result.data),
        error: (err) =>
          setDialog({
            open: true,
            type: "error",
            title: "Error",
            message: "Error parsing CSV file: " + err.message,
            onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
          }),
      });
    } else if (fileExtension === "xlsx") {
      const reader = new FileReader();
      reader.onload = (event) => {
        const data = new Uint8Array(event.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);
        processFile(jsonData);
      };
      reader.onerror = () =>
        setDialog({
          open: true,
          type: "error",
          title: "Error",
          message: "Error reading XLSX file.",
          onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
        });
      reader.readAsArrayBuffer(budgetFile);
    } else {
      setDialog({
        open: true,
        type: "error",
        title: "Error",
        message: "Unsupported file format. Please upload a .csv or .xlsx file.",
        onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
      });
    }
  };

  // =====================================================
  // ADD this helper function inside the BudgetTab component,
  // right before the return statement.
  // Then replace the entire single-view budget list section.
  // =====================================================

  const renderSingleBudgetView = () => {
    // Separate budgets by type
    const originalBudgets = budgets.filter(
      (b) => !b.isVariance && !b.isChangeOrder && !b.isTimeAndMaterial,
    );
    const coBudgets = budgets.filter((b) => b.isChangeOrder && !b.isVariance);
    const tmBudgets = budgets.filter(
      (b) => b.isTimeAndMaterial && !b.isVariance && !b.isChangeOrder,
    );

    // Group variance budgets by their originalBudgetId
    const varianceBudgets = budgets.filter((b) => b.isVariance);
    const variancesByParent = {};
    varianceBudgets.forEach((vb) => {
      const parentId = vb.originalBudgetId?.toString();
      if (parentId) {
        if (!variancesByParent[parentId]) variancesByParent[parentId] = [];
        variancesByParent[parentId].push(vb);
      }
    });

    // All non-variance budgets in display order: originals, then COs, then T&M
    const topLevelBudgets = [...originalBudgets, ...coBudgets, ...tmBudgets];

    const renderBudgetExpandedContent = (budget) => {
      if (expandedBudget !== budget._id) return null;

      return (
        <div className="pb-4">
          <div className="bg-gray-800 rounded-lg p-3 mb-3">
            {editingBudget === budget._id &&
              budget.status !== "confirmed" &&
              selectedLineItems.length > 0 && (
                <button
                  onClick={() => setShowBulkVendor(!showBulkVendor)}
                  disabled={selectedMaterialCount === 0}
                  className={`px-3 py-1.5 text-xs font-medium text-white rounded transition-colors ${
                    selectedMaterialCount > 0
                      ? showBulkVendor
                        ? "bg-orange-600"
                        : "bg-blue-600 hover:bg-blue-700"
                      : "bg-gray-600 cursor-not-allowed"
                  }`}
                >
                  Set Vendor ({selectedMaterialCount})
                </button>
              )}
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-4 text-xl">
                <span className="text-gray-400">
                  <span className="text-white font-semibold">
                    {filteredBudgetItems.length.toLocaleString()}
                  </span>
                  {filteredBudgetItems.length !==
                    currentBudgetRawItems.length && (
                    <span className="text-gray-500">
                      {" "}
                      / {currentBudgetRawItems.length.toLocaleString()}
                    </span>
                  )}{" "}
                  items
                </span>
                <span className="text-gray-600">|</span>
                <span className="text-green-400 font-medium">
                  ${formatNumber(filteredTotals.total)}
                </span>
                <span className="text-gray-600">|</span>
                <span className="text-blue-400">
                  M: ${formatNumber(filteredTotals.materials)}
                </span>
                <span className="text-orange-400">
                  L: ${formatNumber(filteredTotals.labor)}
                </span>
              </div>
              <div className="flex items-center gap-2">
                {budget.status === "confirmed" &&
                  (!budget.isTimeAndMaterial || budget.isChangeOrder) && (
                    <button
                      onClick={handleCreateVariances}
                      disabled={selectedLineItems.length === 0}
                      className={`px-3 py-1.5 text-xs font-medium text-white rounded transition-colors ${
                        selectedLineItems.length > 0
                          ? "bg-orange-500 hover:bg-orange-600"
                          : "bg-gray-600 cursor-not-allowed"
                      }`}
                    >
                      + Variance ({selectedLineItems.length})
                    </button>
                  )}
              </div>

              {editingBudget === budget._id && !budget.isVariance && (
                <div className="mt-3 flex justify-end px-4">
                  <button
                    onClick={saveEditedBudget}
                    disabled={isUpdatingBudget}
                    className={`px-4 py-2 text-sm font-medium text-white rounded-md transition-colors duration-200 ${
                      !isUpdatingBudget
                        ? "bg-orange-400 hover:bg-orange-500"
                        : "bg-gray-600 cursor-not-allowed"
                    }`}
                  >
                    Update Budget
                  </button>
                </div>
              )}
            </div>

            {/* Filter pills */}
            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative">
                <input
                  type="text"
                  value={searchInput}
                  onChange={handleDescriptionSearchChange}
                  placeholder="Search..."
                  className="w-40 px-2 py-1 text-xs bg-gray-700 border border-gray-600 rounded text-gray-200 placeholder-gray-500 focus:border-orange-500 focus:outline-none"
                />
                {searchInput && (
                  <button
                    onClick={() => {
                      setSearchInput("");
                      handleFilterChange("descriptionSearch", "");
                    }}
                    className="absolute right-1 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                  >
                    <svg
                      className="w-3 h-3"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                )}
              </div>
              <span className="text-gray-600">|</span>
              {filters.condition.length > 0 && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] font-medium bg-teal-600/80 text-white rounded">
                  Assembly:{" "}
                  {filters.condition.length === 1
                    ? filters.condition[0]
                    : `${filters.condition.length} selected`}
                  <button
                    onClick={() => handleFilterChange("condition", [])}
                    className="hover:text-teal-200"
                  >
                    ×
                  </button>
                </span>
              )}
              {filters.category.length > 0 && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] font-medium bg-blue-600/80 text-white rounded">
                  {filters.category.join(", ")}
                  <button
                    onClick={() => handleFilterChange("category", [])}
                    className="hover:text-blue-200"
                  >
                    ×
                  </button>
                </span>
              )}
              {filters.section.length > 0 && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] font-medium bg-purple-600/80 text-white rounded">
                  Section:{" "}
                  {filters.section.length === 1
                    ? filters.section[0]
                    : `${filters.section.length} selected`}
                  <button
                    onClick={() => handleFilterChange("section", [])}
                    className="hover:text-purple-200"
                  >
                    ×
                  </button>
                </span>
              )}
              {filters.bidArea.length > 0 && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] font-medium bg-green-600/80 text-white rounded">
                  Area:{" "}
                  {filters.bidArea.length === 1
                    ? filters.bidArea[0]
                    : `${filters.bidArea.length} selected`}
                  <button
                    onClick={() => handleFilterChange("bidArea", [])}
                    className="hover:text-green-200"
                  >
                    ×
                  </button>
                </span>
              )}
              {filters.vendor.length > 0 && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] font-medium bg-yellow-600/80 text-white rounded">
                  Vendor:{" "}
                  {filters.vendor.length === 1
                    ? vendors.find((v) => v._id === filters.vendor[0])
                        ?.companyName || "Unassigned"
                    : `${filters.vendor.length} selected`}
                  <button
                    onClick={() => handleFilterChange("vendor", [])}
                    className="hover:text-yellow-200"
                  >
                    ×
                  </button>
                </span>
              )}
              {filters.uom.length > 0 && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] font-medium bg-indigo-600/80 text-white rounded">
                  UOM:{" "}
                  {filters.uom.length === 1
                    ? filters.uom[0]
                    : `${filters.uom.length} selected`}
                  <button
                    onClick={() => handleFilterChange("uom", [])}
                    className="hover:text-indigo-200"
                  >
                    ×
                  </button>
                </span>
              )}
              {filters.size.length > 0 && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] font-medium bg-pink-600/80 text-white rounded">
                  Size:{" "}
                  {filters.size.length === 1
                    ? filters.size[0]
                    : `${filters.size.length} selected`}
                  <button
                    onClick={() => handleFilterChange("size", [])}
                    className="hover:text-pink-200"
                  >
                    ×
                  </button>
                </span>
              )}
              {filters.poWo.length > 0 && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] font-medium bg-red-600/80 text-white rounded">
                  {filters.poWo.length === 1
                    ? filters.poWo[0]
                    : `${filters.poWo.length} PO/WO`}
                  <button
                    onClick={() => handleFilterChange("poWo", [])}
                    className="hover:text-red-200"
                  >
                    ×
                  </button>
                </span>
              )}
              {(filters.condition.length > 0 ||
                filters.category.length > 0 ||
                filters.section.length > 0 ||
                filters.bidArea.length > 0 ||
                filters.vendor.length > 0 ||
                filters.uom.length > 0 ||
                filters.size.length > 0 ||
                filters.poWo.length > 0 ||
                filters.descriptionSearch) && (
                <button
                  onClick={clearAllFilters}
                  className="px-1.5 py-0.5 text-[11px] font-medium text-red-400 hover:text-red-300 hover:bg-red-900/30 rounded"
                >
                  Clear all
                </button>
              )}
            </div>
          </div>

          {/* Bulk Vendor Assignment Bar */}
          {editingBudget === budget._id &&
            budget.status !== "confirmed" &&
            selectedLineItems.length > 0 &&
            showBulkVendor && (
              <div className="px-4 mb-3">
                <div className="bg-gray-800 border border-orange-500/50 rounded-lg p-3 flex items-center gap-4">
                  <span className="text-sm text-gray-300 whitespace-nowrap">
                    Assign vendor to{" "}
                    <span className="text-orange-400 font-semibold">
                      {selectedMaterialCount}
                    </span>{" "}
                    material item{selectedMaterialCount !== 1 ? "s" : ""}:
                  </span>
                  <div className="flex-1 max-w-xs">
                    <Select
                      options={[
                        { value: null, label: "Unassigned" },
                        ...vendors.map((v) => ({
                          value: v._id,
                          label: v.companyName,
                        })),
                      ]}
                      onChange={(opt) =>
                        handleBulkAssignVendor(opt?.value || null)
                      }
                      placeholder="Select vendor..."
                      isClearable
                      styles={customStyles}
                      menuPortalTarget={document.body}
                    />
                  </div>
                  <button
                    onClick={() => setShowBulkVendor(false)}
                    className="text-gray-400 hover:text-white"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
                {selectedMaterialCount === 0 && (
                  <p className="text-xs text-yellow-400 mt-1">
                    No material items selected. Only material (M) items can have
                    vendors assigned.
                  </p>
                )}
              </div>
            )}

          {/* Loading state */}
          {loadingBudgetDetail && (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
              <span className="ml-3 text-gray-400 text-sm">
                Loading items...
              </span>
            </div>
          )}

          {/* Items table */}
          {!loadingBudgetDetail && (
            <div className="overflow-x-auto rounded-lg border border-gray-700 px-2 isolate">
              <table className="w-full text-[13px] mb-12">
                <thead className="bg-gray-800/95 sticky top-0 z-10">
                  <tr className="border-b border-gray-700">
                    <th className="w-8 px-1 py-1.5 text-center text-[13px] font-medium text-gray-400">
                      <input
                        type="checkbox"
                        checked={allFilteredSelected}
                        onChange={handleSelectAllFiltered}
                        className="h-3 w-3 rounded border-gray-500 text-orange-500"
                      />
                    </th>
                    <th className="w-12 px-1 py-1.5 text-left text-[13px] font-medium text-gray-400">
                      #
                    </th>
                    <th
                      className={`w-28 px-1 py-1.5 text-left text-[13px] font-medium cursor-pointer relative hover:text-orange-300 ${filters.condition.length > 0 ? "text-orange-400" : "text-gray-400"}`}
                      onClick={() => toggleFilter("condition")}
                    >
                      <span className="flex items-center gap-0.5">
                        Assembly
                        <svg
                          className="w-3 h-3"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                        {filters.condition.length > 0 && (
                          <span className="ml-0.5 px-1 py-0.5 text-[9px] bg-orange-500 text-white rounded-full">
                            {filters.condition.length}
                          </span>
                        )}
                      </span>
                      {showFilter === "condition" && (
                        <div
                          ref={conditionRef}
                          className="absolute left-0 top-full z-50 mt-1 bg-gray-700 border border-gray-500 rounded-lg shadow-2xl"
                          style={{
                            minWidth: "280px",
                            width: "max-content",
                            maxWidth: "400px",
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="p-2">
                            <Select
                              isMulti
                              closeMenuOnSelect={false}
                              blurInputOnSelect={false}
                              backspaceRemovesValue={false}
                              hideSelectedOptions={false}
                              menuShouldBlockScroll={false}
                              captureMenuScroll={true}
                              inputValue={conditionSearchText}
                              onInputChange={(newValue, actionMeta) => {
                                if (actionMeta.action === "input-change")
                                  setConditionSearchText(newValue);
                              }}
                              options={getFilterOptions("condition")}
                              value={getFilterOptions("condition").filter(
                                (opt) => filters.condition.includes(opt.value),
                              )}
                              onChange={(options) =>
                                handleFilterChange(
                                  "condition",
                                  options ? options.map((o) => o.value) : [],
                                )
                              }
                              placeholder="Select assemblies..."
                              isClearable
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                              styles={multiSelectFilterStyles}
                            />
                          </div>
                        </div>
                      )}
                    </th>
                    <th
                      className={`w-20 px-1 py-1.5 text-left text-[13px] font-medium cursor-pointer relative hover:text-orange-300 ${filters.section.length > 0 ? "text-orange-400" : "text-gray-400"}`}
                      onClick={() => toggleFilter("section")}
                    >
                      <span className="flex items-center gap-0.5">
                        Section
                        <svg
                          className="w-3 h-3"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                        {filters.section.length > 0 && (
                          <span className="ml-0.5 px-1 py-0.5 text-[9px] bg-orange-500 text-white rounded-full">
                            {filters.section.length}
                          </span>
                        )}
                      </span>
                      {showFilter === "section" && (
                        <div
                          ref={sectionRef}
                          className="absolute left-0 top-full z-50 mt-1 bg-gray-700 border border-gray-500 rounded-lg shadow-2xl"
                          style={{ minWidth: "180px", width: "max-content" }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="p-2">
                            <Select
                              isMulti
                              closeMenuOnSelect={false}
                              hideSelectedOptions={false}
                              options={getFilterOptions("section")}
                              value={getFilterOptions("section").filter((opt) =>
                                filters.section.includes(opt.value),
                              )}
                              onChange={(options) =>
                                handleFilterChange(
                                  "section",
                                  options ? options.map((o) => o.value) : [],
                                )
                              }
                              placeholder="Select sections..."
                              isClearable
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                              styles={multiSelectFilterStyles}
                            />
                          </div>
                        </div>
                      )}
                    </th>
                    <th
                      className={`w-12 px-1 py-1.5 text-left text-[13px] font-medium cursor-pointer relative hover:text-orange-300 ${filters.category.length > 0 ? "text-orange-400" : "text-gray-400"}`}
                      onClick={() => toggleFilter("category")}
                    >
                      <span className="flex items-center gap-0.5">
                        Cat
                        <svg
                          className="w-3 h-3"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                        {filters.category.length > 0 && (
                          <span className="ml-0.5 px-1 py-0.5 text-[9px] bg-orange-500 text-white rounded-full">
                            {filters.category.length}
                          </span>
                        )}
                      </span>
                      {showFilter === "category" && (
                        <div
                          ref={categoryRef}
                          className="absolute left-0 top-full z-50 mt-1 bg-gray-700 border border-gray-500 rounded-lg shadow-2xl"
                          style={{ minWidth: "150px", width: "max-content" }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="p-2">
                            <Select
                              isMulti
                              closeMenuOnSelect={false}
                              hideSelectedOptions={false}
                              options={getFilterOptions("category")}
                              value={getFilterOptions("category").filter(
                                (opt) => filters.category.includes(opt.value),
                              )}
                              onChange={(options) =>
                                handleFilterChange(
                                  "category",
                                  options ? options.map((o) => o.value) : [],
                                )
                              }
                              placeholder="Select categories..."
                              isClearable
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                              styles={multiSelectFilterStyles}
                            />
                          </div>
                        </div>
                      )}
                    </th>
                    <th className="min-w-[180px] px-1 py-1.5 text-left text-[13px] font-medium text-gray-400">
                      Description
                    </th>
                    <th
                      className={`w-24 px-1 py-1.5 text-left text-[13px] font-medium cursor-pointer relative hover:text-orange-300 ${filters.size.length > 0 ? "text-orange-400" : "text-gray-400"}`}
                      onClick={() => toggleFilter("size")}
                    >
                      <span className="flex items-center gap-0.5">
                        Size
                        <svg
                          className="w-3 h-3"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                        {filters.size.length > 0 && (
                          <span className="ml-0.5 px-1 py-0.5 text-[9px] bg-orange-500 text-white rounded-full">
                            {filters.size.length}
                          </span>
                        )}
                      </span>
                      {showFilter === "size" && (
                        <div
                          ref={sizeRef}
                          className="absolute left-0 top-full z-50 mt-1 bg-gray-700 border border-gray-500 rounded-lg shadow-2xl"
                          style={{ minWidth: "150px", width: "max-content" }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="p-2">
                            <Select
                              isMulti
                              closeMenuOnSelect={false}
                              hideSelectedOptions={false}
                              options={getFilterOptions("size")}
                              value={getFilterOptions("size").filter((opt) =>
                                filters.size.includes(opt.value),
                              )}
                              onChange={(options) =>
                                handleFilterChange(
                                  "size",
                                  options ? options.map((o) => o.value) : [],
                                )
                              }
                              placeholder="Select sizes..."
                              isClearable
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                              styles={multiSelectFilterStyles}
                            />
                          </div>
                        </div>
                      )}
                    </th>
                    <th className="w-16 px-1 py-1.5 text-right text-[13px] font-medium text-gray-400">
                      Qty
                    </th>
                    <th
                      className={`w-16 px-1 py-1.5 text-center text-[13px] font-medium cursor-pointer relative hover:text-orange-300 ${filters.uom.length > 0 ? "text-orange-400" : "text-gray-400"}`}
                      onClick={() => toggleFilter("uom")}
                    >
                      <span className="flex items-center gap-0.5">
                        UOM
                        <svg
                          className="w-3 h-3"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                        {filters.uom.length > 0 && (
                          <span className="ml-0.5 px-1 py-0.5 text-[9px] bg-orange-500 text-white rounded-full">
                            {filters.uom.length}
                          </span>
                        )}
                      </span>
                      {showFilter === "uom" && (
                        <div
                          ref={uomRef}
                          className="absolute left-0 top-full z-50 mt-1 bg-gray-700 border border-gray-500 rounded-lg shadow-2xl"
                          style={{ minWidth: "150px", width: "max-content" }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="p-2">
                            <Select
                              isMulti
                              closeMenuOnSelect={false}
                              hideSelectedOptions={false}
                              options={getFilterOptions("uom")}
                              value={getFilterOptions("uom").filter((opt) =>
                                filters.uom.includes(opt.value),
                              )}
                              onChange={(options) =>
                                handleFilterChange(
                                  "uom",
                                  options ? options.map((o) => o.value) : [],
                                )
                              }
                              placeholder="Select UOMs..."
                              isClearable
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                              styles={multiSelectFilterStyles}
                            />
                          </div>
                        </div>
                      )}
                    </th>
                    <th className="w-28 px-1 py-1.5 text-right text-[13px] font-medium text-gray-400">
                      Cost/Rate
                    </th>
                    <th className="w-28 px-1 py-1.5 text-right text-[13px] font-medium text-gray-400">
                      Subtotal
                    </th>
                    <th
                      className={`w-32 px-1 py-1.5 text-left text-[13px] font-medium cursor-pointer relative hover:text-orange-300 ${filters.bidArea.length > 0 ? "text-orange-400" : "text-gray-400"}`}
                      onClick={() => toggleFilter("bidArea")}
                    >
                      <span className="flex items-center gap-0.5">
                        Bid Area
                        <svg
                          className="w-3 h-3"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                        {filters.bidArea.length > 0 && (
                          <span className="ml-0.5 px-1 py-0.5 text-[9px] bg-orange-500 text-white rounded-full">
                            {filters.bidArea.length}
                          </span>
                        )}
                      </span>
                      {showFilter === "bidArea" && (
                        <div
                          ref={bidAreaRef}
                          className="absolute left-0 top-full z-50 mt-1 bg-gray-700 border border-gray-500 rounded-lg shadow-2xl"
                          style={{ minWidth: "320px", width: "max-content" }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="p-2">
                            <Select
                              isMulti
                              closeMenuOnSelect={false}
                              hideSelectedOptions={false}
                              options={getFilterOptions("bidArea")}
                              value={getFilterOptions("bidArea").filter((opt) =>
                                filters.bidArea.includes(opt.value),
                              )}
                              onChange={(options) =>
                                handleFilterChange(
                                  "bidArea",
                                  options ? options.map((o) => o.value) : [],
                                )
                              }
                              placeholder="Select areas..."
                              isClearable
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                              styles={multiSelectFilterStyles}
                            />
                          </div>
                        </div>
                      )}
                    </th>
                    <th
                      className={`w-40 px-1 py-1.5 text-left text-[13px] font-medium cursor-pointer relative hover:text-orange-300 ${filters.vendor.length > 0 ? "text-orange-400" : "text-gray-400"}`}
                      onClick={() => toggleFilter("vendor")}
                    >
                      <span className="flex items-center gap-0.5">
                        Vendor
                        <svg
                          className="w-3 h-3"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                        {filters.vendor.length > 0 && (
                          <span className="ml-0.5 px-1 py-0.5 text-[9px] bg-orange-500 text-white rounded-full">
                            {filters.vendor.length}
                          </span>
                        )}
                      </span>
                      {showFilter === "vendor" && (
                        <div
                          ref={vendorRef}
                          className="absolute left-0 top-full z-50 mt-1 bg-gray-700 border border-gray-500 rounded-lg shadow-2xl"
                          style={{ minWidth: "225px", width: "max-content" }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="p-2">
                            <Select
                              isMulti
                              closeMenuOnSelect={false}
                              hideSelectedOptions={false}
                              options={getFilterOptions("vendor")}
                              value={getFilterOptions("vendor").filter((opt) =>
                                filters.vendor.includes(opt.value),
                              )}
                              onChange={(options) =>
                                handleFilterChange(
                                  "vendor",
                                  options ? options.map((o) => o.value) : [],
                                )
                              }
                              placeholder="Select vendors..."
                              isClearable
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                              styles={multiSelectFilterStyles}
                            />
                          </div>
                        </div>
                      )}
                    </th>
                    <th className="w-32 px-2 py-1.5 text-left text-[13px] font-medium text-gray-400">
                      PO / WO #
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700/50">
                  {paginatedItems.map((item, index) => {
                    const originalIndex = (
                      editingBudget === budget._id
                        ? editedItems
                        : budget.lineItems || []
                    ).findIndex((i) => i._id === item._id);
                    const vendorId =
                      editingBudget === budget._id
                        ? item.vendor
                        : item.vendor &&
                            typeof item.vendor === "object" &&
                            item.vendor._id
                          ? item.vendor._id
                          : typeof item.vendor === "string"
                            ? item.vendor
                            : null;
                    const displayQty = getDisplayQty(item);
                    const displayPrice = getDisplayPrice(item);

                    return (
                      <React.Fragment
                        key={item._id || item.key || `item-${index}`}
                      >
                        <tr
                          className={`hover:bg-gray-700/50 transition-colors cursor-pointer ${
                            index % 2 === 0
                              ? "bg-gray-800/50"
                              : "bg-gray-400/10"
                          } ${
                            selectedLineItems.some(
                              (i) =>
                                getItemSelectionKey(i) ===
                                getItemSelectionKey(item),
                            )
                              ? "ring-1 ring-orange-500/50 bg-orange-900/20"
                              : ""
                          } ${item.isHistoricalPriceLine ? "bg-gray-800/70 border-l-4 border-gray-500" : ""}
                        ${item.isPriceChangeRow && !item.isHistoricalPriceLine ? "bg-purple-900/15 border-l-4 border-purple-500" : ""}
                        ${item.closedOutBySizeChange ? "bg-yellow-900/10" : ""}`}
                          onClick={() =>
                            setExpandedItemId(
                              expandedItemId === (item._id || item.key)
                                ? null
                                : item._id || item.key,
                            )
                          }
                        >
                          <td className="w-8 px-1 py-1 text-center">
                            <input
                              type="checkbox"
                              checked={selectedLineItems.some(
                                (i) =>
                                  getItemSelectionKey(i) ===
                                  getItemSelectionKey(item),
                              )}
                              onChange={() => handleSelectLineItem(item)}
                              onClick={(e) => e.stopPropagation()}
                              className="h-3 w-3 text-orange-500 focus:ring-orange-500 border-gray-500 rounded"
                            />
                          </td>
                          <td className="w-12 px-1 py-1 text-[13px] text-gray-400 font-mono">
                            {item.itemNumberDisplay
                              ?.toFixed(2)
                              .replace(/\.00$/, "") ||
                              item.itemNumber ||
                              originalIndex + 1}
                          </td>
                          <td className="w-32 px-1 py-1 text-[13px] text-gray-300">
                            <div className="flex flex-col">
                              <span className="break-words">
                                {item.conditionName || (
                                  <span className="text-gray-600">—</span>
                                )}
                              </span>
                              <ItemStatusBadges item={item} />
                              {item.note && (
                                <span className="text-[10px] text-amber-400 italic mt-0.5">
                                  {item.note}
                                </span>
                              )}
                            </div>
                          </td>
                          <td
                            className="w-20 px-1 py-1 text-[13px] text-gray-300 truncate"
                            title={item.sectionNumber}
                          >
                            {item.sectionNumber || "—"}
                          </td>
                          <td className="w-10 px-1 py-1 text-center">
                            <span
                              className={`inline-block px-1.5 py-0.5 text-[12px] font-semibold rounded ${item.category === "M" ? "bg-blue-900/50 text-blue-300" : "bg-orange-900/50 text-orange-300"}`}
                            >
                              {item.category}
                            </span>
                          </td>
                          <td
                            className="w-48 px-1 py-1 text-[13px] text-gray-200"
                            title={
                              item.itemDescription || item.costCodeDescription
                            }
                          >
                            <div className="max-w-[250px] break-words">
                              {item.itemDescription ||
                                item.costCodeDescription ||
                                "—"}
                            </div>
                          </td>
                          <td className="w-24 px-1 py-1 text-[13px] text-gray-400">
                            <div className="flex flex-col">
                              <span>{item.size || item.latestSize || "—"}</span>
                              {item.isNewSpec && item.sizeChangedFrom && (
                                <span className="text-[10px] text-blue-400">
                                  was: {item.sizeChangedFrom}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-2 py-1 text-[13px] text-right">
                            <div className="flex flex-col items-end">
                              <span
                                className={`font-mono font-semibold ${item.isHistoricalPriceLine ? "text-gray-400" : "text-gray-200"}`}
                              >
                                {displayQty}
                              </span>
                              {item.baseRequiredQty !== undefined &&
                                item.baseRequiredQty !== displayQty &&
                                !item.isHistoricalPriceLine && (
                                  <span className="text-xs text-gray-500">
                                    base: {item.baseRequiredQty}
                                  </span>
                                )}
                              {item.coTotalDeltaQty !== undefined &&
                                item.coTotalDeltaQty !== 0 &&
                                !item.isHistoricalPriceLine && (
                                  <span
                                    className={`text-xs ${item.coTotalDeltaQty > 0 ? "text-red-400" : "text-green-400"}`}
                                  >
                                    {item.coTotalDeltaQty > 0 ? "+" : ""}
                                    {item.coTotalDeltaQty} Δ
                                  </span>
                                )}
                            </div>
                          </td>
                          <td
                            className="w-14 px-1 py-1 text-[13px] text-center"
                            title={item.uom || item.container}
                          >
                            <span
                              className={`inline-block px-1.5 py-0.5 text-[13px] font-medium rounded ${
                                getDisplayUom(item) === "PC"
                                  ? "bg-teal-900/50 text-teal-300"
                                  : "bg-gray-700/60 text-gray-400"
                              }`}
                            >
                              {getDisplayUom(item)}
                            </span>
                          </td>
                          <td className="w-24 px-1 py-1 text-[13px] text-right">
                            <div className="flex flex-col items-end gap-0.5">
                              <span
                                className={`font-mono ${item.isHistoricalPriceLine ? "text-gray-400" : item.isPriceChangeRow ? "text-purple-300" : "text-gray-200"}`}
                              >
                                ${formatNumber(getDisplayPackageCost(item))}
                              </span>
                              {(() => {
                                const uom = (item.uom || item.container || "")
                                  .trim()
                                  .toUpperCase();
                                const isCoverageBased = ["SF", "LF"].some((u) =>
                                  uom.includes(u),
                                );
                                return isCoverageBased &&
                                  item.category === "M" ? (
                                  <span className="text-[12px] text-gray-500">
                                    ${formatNumber(displayPrice)}/{uom}
                                  </span>
                                ) : null;
                              })()}
                              {item.priceChangedFrom &&
                                !item.isHistoricalPriceLine &&
                                item.priceChangedFrom !== displayPrice && (
                                  <span className="text-[12px] text-gray-500">
                                    was ${formatNumber(item.priceChangedFrom)}
                                  </span>
                                )}
                            </div>
                          </td>
                          <td className="w-32 px-1 py-1 text-[13px] text-right font-medium">
                            <div className="flex flex-col items-end">
                              <span
                                className={`font-mono ${item.isHistoricalPriceLine ? "text-gray-400" : item.category === "M" ? "text-blue-300" : "text-orange-300"}`}
                              >
                                ${formatNumber(item.amount || 0)}
                              </span>
                              {item.originalAmount != null &&
                                Math.abs(
                                  item.originalAmount - (item.amount || 0),
                                ) > 1 &&
                                !item.isHistoricalPriceLine && (
                                  <span className="text-xs text-gray-500">
                                    was ${formatNumber(item.originalAmount)}
                                  </span>
                                )}
                            </div>
                          </td>
                          <td
                            className="w-32 px-1 py-1 text-[13px] text-gray-400 truncate"
                            title={item.bidArea}
                          >
                            {item.bidArea || "—"}
                          </td>
                          <td className="w-40 px-1 py-1 text-[13px] text-gray-400 truncate">
                            {item.category === "M"
                              ? vendors.find((v) => v._id === vendorId)
                                  ?.companyName || "—"
                              : "—"}
                          </td>
                          <td className="w-40 px-3 py-2 text-[13px]">
                            {item.category === "M" && item.linkedPO ? (
                              (() => {
                                const po = project.purchaseOrders2?.find(
                                  (p) =>
                                    String(p._id) === String(item.linkedPO),
                                );
                                return po?.poNumber ? (
                                  <span className="inline-flex items-center gap-1.5 text-blue-300 font-medium">
                                    <span className="text-xs text-blue-400">
                                      PO -
                                    </span>
                                    {po.poNumber}
                                  </span>
                                ) : (
                                  <span className="text-amber-400">
                                    PO (missing)
                                  </span>
                                );
                              })()
                            ) : item.category === "L" && item.linkedWO ? (
                              (() => {
                                const wo = project.workOrders2?.find(
                                  (w) =>
                                    String(w._id) === String(item.linkedWO),
                                );
                                return wo?.woNumber ? (
                                  <span className="inline-flex items-center gap-1.5 text-purple-300 font-medium">
                                    {wo.woNumber}
                                  </span>
                                ) : (
                                  <span className="text-amber-400">
                                    WO (missing)
                                  </span>
                                );
                              })()
                            ) : (
                              <span className="text-gray-500">—</span>
                            )}
                          </td>
                        </tr>
                        {expandedItemId === (item._id || item.key) &&
                          renderVarianceSummary(item)}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 py-4">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="px-2 py-1 text-[13px] bg-gray-700 text-gray-400 rounded disabled:opacity-30 hover:bg-gray-600 hover:text-white"
              >
                ««
              </button>
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-2 py-1 text-[13px] bg-gray-700 text-white rounded disabled:opacity-30 hover:bg-gray-600 hover:text-white"
              >
                ‹ Prev
              </button>
              <div className="flex items-center gap-1">
                <span className="text-[13px] text-white">Page</span>
                <select
                  value={currentPage}
                  onChange={(e) => setCurrentPage(Number(e.target.value))}
                  className="px-6 py-0.5 text-[13px] bg-gray-700 border border-gray-600 rounded text-gray-200"
                >
                  {Array.from({ length: totalPages }, (_, i) => (
                    <option key={i + 1} value={i + 1}>
                      {i + 1}
                    </option>
                  ))}
                </select>
                <span className="text-[13px] text-white">of {totalPages}</span>
              </div>
              <button
                onClick={() =>
                  setCurrentPage((p) => Math.min(totalPages, p + 1))
                }
                disabled={currentPage === totalPages}
                className="px-2 py-1 text-[13px] bg-gray-700 text-white rounded disabled:opacity-30 hover:bg-gray-600 hover:text-white"
              >
                Next ›
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className="px-2 py-1 text-[13px] bg-gray-700 text-white rounded disabled:opacity-30 hover:bg-gray-600 hover:text-white"
              >
                »»
              </button>
            </div>
          )}
        </div>
      );
    };

    const renderVarianceBudgetCard = (vBudget) => {
      const vTotal = getBudgetDisplayTotal(vBudget._id);
      const isPositive = vTotal > 0;
      const isNegative = vTotal < 0;

      return (
        <div
          key={vBudget._id}
          className={`border rounded-lg shadow-sm ${
            isPositive
              ? "border-red-700/40 bg-red-950/10"
              : isNegative
                ? "border-green-700/40 bg-green-950/10"
                : "border-amber-700/40 bg-amber-950/10"
          }`}
        >
          <div className="px-4 py-3 flex justify-between items-center">
            <div className="flex items-center gap-2">
              <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-amber-600/80 text-amber-100 border border-amber-400/50">
                VARIANCE
              </span>
              <div>
                <h5 className="text-sm font-medium text-gray-200">
                  {vBudget.title}
                </h5>
                {vBudget.varianceReason && (
                  <p className="text-xs text-gray-500 italic mt-0.5">
                    {vBudget.varianceReason}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span
                className={`text-sm font-semibold ${isPositive ? "text-red-400" : isNegative ? "text-green-400" : "text-gray-400"}`}
              >
                {isPositive ? "+" : ""}$
                {Math.abs(vTotal).toLocaleString("en-US", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </span>
              <button
                onClick={() => toggleBudgetItems(vBudget._id)}
                className="px-2 py-1 text-xs font-medium text-gray-300 bg-gray-700 rounded hover:bg-gray-600 transition-colors"
              >
                {expandedBudget === vBudget._id ? "Collapse" : "Expand"}
              </button>
            </div>
          </div>

          {renderBudgetExpandedContent(vBudget)}
        </div>
      );
    };

    return (
      <div className="space-y-4">
        {topLevelBudgets.map((budget) => {
          const budgetTotal = getBudgetDisplayTotal(budget._id).toLocaleString(
            "en-US",
            {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            },
          );

          const relatedVariances =
            variancesByParent[budget._id.toString()] || [];

          return (
            <div key={budget._id} className="space-y-0">
              {/* Main budget card */}
              <div className="border border-gray-600 rounded-lg shadow-lg bg-gray-700">
                <div className="px-6 py-4 flex justify-between items-center">
                  <div className="flex items-center space-x-3">
                    {budget.isTimeAndMaterial && !budget.isChangeOrder && (
                      <input
                        type="checkbox"
                        checked={selectedBudgets.includes(budget._id)}
                        onChange={() => handleSelectBudget(budget._id)}
                        className="h-4 w-4 text-orange-400 focus:ring-orange-500 border-gray-500 rounded"
                      />
                    )}
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className="text-md font-semibold text-white">
                          {budget.title} -
                        </h4>
                        {budget.isChangeOrder && (
                          <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-purple-600/80 text-purple-100 border border-purple-400/50">
                            CO
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-400">
                        Total Amount: ${budgetTotal}
                        {relatedVariances.length > 0 && (
                          <span className="ml-2 text-amber-400 text-xs">
                            ({relatedVariances.length} variance
                            {relatedVariances.length !== 1 ? "s" : ""})
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => toggleBudgetItems(budget._id)}
                      className="px-3 py-1 text-sm font-medium text-white bg-gray-600 rounded-md hover:bg-gray-500 transition-colors duration-200"
                    >
                      {expandedBudget === budget._id ? "Collapse" : "Expand"}
                    </button>
                    {budget.status === "pending" && (
                      <button
                        onClick={() => handleConfirmBudgetPrompt(budget._id)}
                        className="px-3 py-1 text-sm font-medium text-white bg-orange-400 rounded-md hover:bg-orange-500 transition-colors duration-200"
                      >
                        Confirm
                      </button>
                    )}
                    {!budget.isChangeOrder && budget.status !== "confirmed" && (
                      <button
                        onClick={() =>
                          editingBudget === budget._id
                            ? handleCancelEdit()
                            : handleEditBudget(budget._id)
                        }
                        className={`px-3 py-1 text-sm font-medium text-white rounded-md transition-colors duration-200 ${
                          editingBudget === budget._id
                            ? "bg-gray-600 hover:bg-gray-500"
                            : "bg-orange-400 hover:bg-orange-500"
                        }`}
                      >
                        {editingBudget === budget._id
                          ? "Cancel"
                          : "Edit Budget"}
                      </button>
                    )}
                    {!budget.isChangeOrder && budget.status !== "confirmed" && (
                      <button
                        onClick={() => handleDeleteBudgetPrompt(budget._id)}
                        className="p-2 text-red-400 hover:text-red-300 transition-colors duration-200"
                        title="Delete Budget"
                      >
                        <FiTrash2 size={18} />
                      </button>
                    )}
                  </div>
                </div>

                {renderBudgetExpandedContent(budget)}
              </div>

              {/* Nested variance budgets for this parent */}
              {relatedVariances.length > 0 && (
                <div className="ml-6 mt-2 space-y-2">
                  {relatedVariances.map((vBudget) =>
                    renderVarianceBudgetCard(vBudget),
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // Legacy upload function
  const uploadBudget = async (lineItems) => {
    setIsUploading(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `https://erp-production-72da01c8e651.herokuapp.com/api/projects/${project._id}/budgets`,
        { title: budgetTitle, lineItems },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const updatedProjectResponse = await axios.get(
        `https://erp-production-72da01c8e651.herokuapp.com/api/projects/${project._id}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setProject(updatedProjectResponse.data);
      setBudgetTitle("");
      setBudgetFile(null);
      setShowBudgetForm(false);
      setDialog({
        open: true,
        type: "success",
        title: "Success",
        message: "Budget uploaded successfully!",
        onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
      });
    } catch (err) {
      setDialog({
        open: true,
        type: "error",
        title: "Error",
        message:
          "Failed to upload budget: " +
          (err.response?.data?.message || err.message),
        onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
      });
    } finally {
      setIsUploading(false);
    }
  };

  // V2 upload function - uses Budget2 model
  const uploadBudgetV2 = async (lineItems) => {
    setIsUploading(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `https://erp-production-72da01c8e651.herokuapp.com/api/budget2/${project._id}/`,
        { title: budgetTitle, lineItems },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const updatedProjectResponse = await axios.get(
        `https://erp-production-72da01c8e651.herokuapp.com/api/projects/${project._id}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setProject(updatedProjectResponse.data);
      setBudgetTitle("");
      setBudgetFile(null);
      setShowBudgetForm(false);
      setDialog({
        open: true,
        type: "success",
        title: "Success",
        message: "Budget uploaded successfully!",
        onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
      });
    } catch (err) {
      setDialog({
        open: true,
        type: "error",
        title: "Error",
        message:
          "Failed to upload budget: " +
          (err.response?.data?.message || err.message),
        onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleConfirmBudgetPrompt = (budgetId) => {
    setDialog({
      open: true,
      type: "warning",
      title: "Confirm Budget",
      message:
        "Are you sure you want to confirm this budget? Once confirmed, no further changes can be made directly to this budget. Any future modifications will require creating a variance.",
      confirmText: "Yes, Confirm",
      cancelText: "No, Cancel",
      onConfirm: async () => {
        setDialog((prev) => ({ ...prev, open: false }));
        if (editingBudget === budgetId) {
          await saveEditedBudget();
        }
        handleConfirmBudget(budgetId);
      },
      onClose: () => setDialog((prev) => ({ ...prev, open: false })),
    });
  };

  const handleConfirmBudget = async (budgetId) => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `https://erp-production-72da01c8e651.herokuapp.com/api/budget2/${project._id}/${budgetId}/confirm`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const updatedProjectResponse = await axios.get(
        `https://erp-production-72da01c8e651.herokuapp.com/api/projects/${project._id}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setProject(updatedProjectResponse.data);
      setDialog({
        open: true,
        type: "success",
        title: "Success",
        message: "Budget confirmed successfully!",
        onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
      });
    } catch (err) {
      setDialog({
        open: true,
        type: "error",
        title: "Error",
        message:
          "Failed to confirm budget: " +
          (err.response?.data?.message || err.message),
        onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
      });
    }
  };

  const handleDeleteBudgetPrompt = (budgetId) => {
    setDialog({
      open: true,
      type: "warning",
      title: "Delete Budget",
      message:
        "Are you sure you want to delete this budget? This action cannot be undone.",
      confirmText: "Yes, Delete",
      cancelText: "No, Cancel",
      onConfirm: () => {
        setDialog((prev) => ({ ...prev, open: false }));
        handleDeleteBudget(budgetId);
      },
      onClose: () => setDialog((prev) => ({ ...prev, open: false })),
    });
  };

  // Add this helper near the top of the component (near other helpers like cleanNumber)
  const normalizeConditionName = (conditionName) => {
    if (!conditionName) return conditionName;
    const str = String(conditionName).trim();
    // Strip leading "number - " or "number-" prefix (e.g., "1 - MS.12a" → "MS.12a", "159-MS.12a" → "MS.12a")
    const match = str.match(/^\d+\s*[-–—]\s*(.+)$/);
    return match ? match[1].trim() : str;
  };

  const handleDeleteBudget = async (budgetId) => {
    try {
      const token = localStorage.getItem("token");
      await axios.delete(
        `https://erp-production-72da01c8e651.herokuapp.com/api/projects/${project._id}/budgets/${budgetId}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const updatedProjectResponse = await axios.get(
        `https://erp-production-72da01c8e651.herokuapp.com/api/projects/${project._id}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setProject(updatedProjectResponse.data);
      setExpandedBudget(null);
      setEditingBudget(null);
      setEditedItems([]);
      setSelectedLineItems([]);
      setSelectedBudgets(selectedBudgets.filter((id) => id !== budgetId));
      setDialog({
        open: true,
        type: "success",
        title: "Success",
        message: "Budget deleted successfully!",
        onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
      });
    } catch (err) {
      setDialog({
        open: true,
        type: "error",
        title: "Error",
        message:
          "Failed to delete budget: " +
          (err.response?.data?.message || err.message),
        onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
      });
    }
  };

  const handleSelectBudget = (budgetId) => {
    setSelectedBudgets((prev) =>
      prev.includes(budgetId)
        ? prev.filter((id) => id !== budgetId)
        : [...prev, budgetId],
    );
  };

  const handleEditBudget = (budgetId) => {
    const budget = budgets.find((b) => b._id === budgetId);
    setEditingBudget(budgetId);
    const normalizedItems = budget.lineItems.map((item) => {
      let vendorId = null;
      if (item.vendor) {
        if (typeof item.vendor === "object" && item.vendor._id) {
          vendorId = item.vendor._id;
        } else if (typeof item.vendor === "string") {
          vendorId = item.vendor;
        }
      }
      const unitRate =
        item.category === "L" && item.quantity > 0
          ? Number((item.amount / item.quantity).toFixed(2))
          : item.unitRate || 0;
      return {
        ...item,
        vendor: vendorId,
        ...(item.category === "L" && { unitRate }),
      };
    });
    setEditedItems([...normalizedItems]);
    setExpandedBudget(budgetId);
  };

  const handleCancelEdit = () => {
    setEditingBudget(null);
    setEditedItems([]);
    setExpandedBudget(null);
    setSelectedLineItems([]);
    clearAllFilters();
  };

  // Helper function to convert feet/inches notation to decimal feet
  // e.g., "4'" -> 4, "4'6\"" -> 4.5, "4.5'" -> 4.5, "48\"" -> 4
  const parseToFeet = (str) => {
    if (!str) return 0;
    const s = String(str).trim();

    // Check for feet and inches: 4'6" or 4' 6"
    const feetInchMatch = s.match(/^([\d.]+)'?\s*([\d.]+)"?$/);
    if (feetInchMatch) {
      const feet = parseFloat(feetInchMatch[1]) || 0;
      const inches = parseFloat(feetInchMatch[2]) || 0;
      return feet + inches / 12;
    }

    // Check for just feet: 4' or 4
    const feetMatch = s.match(/^([\d.]+)'?$/);
    if (feetMatch) {
      return parseFloat(feetMatch[1]) || 0;
    }

    // Check for just inches: 48"
    const inchMatch = s.match(/^([\d.]+)"$/);
    if (inchMatch) {
      return (parseFloat(inchMatch[1]) || 0) / 12;
    }

    // Plain number
    return parseFloat(s) || 0;
  };

  // Helper function to parse size dimensions for SF items
  // e.g., "4'x8'" -> {width: 4, height: 8}, "4x8" -> {width: 4, height: 8}
  const parseSizeDimensions = (sizeStr) => {
    if (!sizeStr) return null;
    const str = String(sizeStr).trim();

    // Match patterns like "4'x8'", "4' x 8'", "4x8", "4.5'x8'", "4'6\"x8'"
    // Split by 'x' or 'X' (case insensitive)
    const parts = str.split(/\s*[xX]\s*/);
    if (parts.length === 2) {
      const width = parseToFeet(parts[0]);
      const height = parseToFeet(parts[1]);
      if (width > 0 && height > 0) {
        return { width, height };
      }
    }
    return null;
  };

  // Helper function to parse LF size (single dimension)
  // e.g., "8'" -> 8, "10" -> 10, "8'6\"" -> 8.5
  const parseLFSize = (sizeStr) => {
    if (!sizeStr) return 0;
    return parseToFeet(sizeStr);
  };

  // Helper function to calculate coverage based on UoM and size
  const calculateCoverage = (size, uom) => {
    const uomLower = (uom || "").toLowerCase(); // Square feet - need width x height
    if (
      uomLower === "sf" ||
      uomLower.includes("square") ||
      uomLower.includes("sq ft") ||
      uomLower.includes("sq.ft")
    ) {
      const dims = parseSizeDimensions(size);
      if (dims) {
        return dims.width * dims.height;
      }
      return null;
    } // Linear feet - single dimension
    if (
      uomLower === "lf" ||
      uomLower.includes("linear") ||
      uomLower.includes("lin ft") ||
      uomLower.includes("lin.ft")
    ) {
      const length = parseLFSize(size);
      if (length > 0) {
        return length;
      }
      return null;
    }
    return null;
  }; // Check if UoM is SF type

  const handleBulkAssignVendor = useCallback(
    (vendorId) => {
      setEditedItems((prev) => {
        const selectedIds = new Set(selectedLineItems.map((i) => i._id));
        return prev.map((item) => {
          // Only update selected material items
          if (!selectedIds.has(item._id) || item.category !== "M") return item;
          return { ...item, vendor: vendorId };
        });
      });

      const materialCount = selectedLineItems.filter(
        (i) => i.category === "M",
      ).length;

      setDialog({
        open: true,
        type: "success",
        title: "Vendor Assigned",
        message: `Vendor updated for ${materialCount} material item${
          materialCount !== 1 ? "s" : ""
        }.`,
        onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
      });

      setSelectedLineItems([]);
      setShowBulkVendor(false);
    },
    [selectedLineItems],
  );

  const saveEditedBudget = async () => {
    setIsUpdatingBudget(true);
    try {
      const token = localStorage.getItem("token");
      const budgetToUpdate = budgets.find((b) => b._id === editingBudget);

      // Don't recalculate amount - just use the existing values
      // handleItemChange already updates amount when qty/cost is edited
      const normalizedLineItems = editedItems.map((item) => ({
        ...item,
        vendor: item.category === "M" ? item.vendor || null : null,
      }));

      await axios.put(
        `https://erp-production-72da01c8e651.herokuapp.com/api/budget2/${project._id}/${editingBudget}`,
        {
          title: budgetToUpdate.title,
          lineItems: normalizedLineItems,
          status: budgetToUpdate.status,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const updatedProjectResponse = await axios.get(
        `https://erp-production-72da01c8e651.herokuapp.com/api/projects/${project._id}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setProject(updatedProjectResponse.data);
      setEditingBudget(null);
      setEditedItems([]);
      setExpandedBudget(null);
      setSelectedLineItems([]);
      clearAllFilters();
      setDialog({
        open: true,
        type: "success",
        title: "Success",
        message: "Budget saved successfully with your changes!",
        onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
      });
    } catch (err) {
      setDialog({
        open: true,
        type: "error",
        title: "Error",
        message:
          "Failed to save budget changes: " +
          (err.response?.data?.message || err.message),
        onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
      });
    } finally {
      setIsUpdatingBudget(false);
    }
  };

  // PERFORMANCE: Memoized select handler
  const handleSelectLineItem = useCallback(
    (item) => {
      const itemKey = getItemSelectionKey(item);
      setSelectedLineItems((prev) => {
        const isSelected = prev.some((i) => getItemSelectionKey(i) === itemKey);
        if (isSelected) {
          return prev.filter((i) => getItemSelectionKey(i) !== itemKey);
        } else {
          return [...prev, item];
        }
      });
    },
    [getItemSelectionKey],
  );

  const selectedMaterialCount = useMemo(() => {
    return selectedLineItems.filter((i) => i.category === "M").length;
  }, [selectedLineItems]);

  const handleCreateVariances = () => {
    if (selectedLineItems.length === 0) {
      setDialog({
        open: true,
        type: "error",
        title: "Error",
        message: "Please select at least one line item to create variances.",
        onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
      });
      return;
    }
    const budget = budgets.find((b) => b._id === expandedBudget);
    if (budget.isChangeOrder) {
      const hasCriticalChanges = selectedLineItems.some(
        (item) =>
          (item.category === "M" &&
            (item.requiredQty !== undefined ||
              item.cost !== undefined ||
              item.price !== undefined)) ||
          (item.category === "L" &&
            (item.quantity !== undefined || item.unitRate !== undefined)),
      );
      if (hasCriticalChanges) {
        setDialog({
          open: true,
          type: "warning",
          title: "Variance Impacts Change Order",
          message:
            "Creating a variance for this change order budget will update the associated change order's quantities, costs, or prices. Do you want to proceed?",
          confirmText: "Yes, Proceed",
          cancelText: "No, Cancel",
          onConfirm: () => {
            setDialog((prev) => ({ ...prev, open: false }));
            setIsAddingToVariance(false);
            setShowVarianceModal(true);
          },
          onClose: () => setDialog((prev) => ({ ...prev, open: false })),
        });
        return;
      }
    }
    setIsAddingToVariance(false);
    setShowVarianceModal(true);
  };

  const multiSelectFilterStyles = {
    control: (provided, state) => ({
      ...provided,
      backgroundColor: "#1f2937",
      borderColor: state.isFocused ? "#f97316" : "#4b5563",
      color: "#e5e7eb",
      boxShadow: "none",
      minHeight: "50px",
      zIndex: 100000,
      fontSize: "14px",
      "&:hover": { borderColor: "#f97316" },
    }),
    input: (provided) => ({
      ...provided,
      color: "#e5e7eb",
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "#9ca3af",
      fontSize: "14px",
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 999999, // Keeps dropdown above everything
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: "#374151",
      borderRadius: "0.5rem",
      boxShadow: "0 10px 40px rgba(0, 0, 0, 0.5)",
      border: "1px solid #4b5563",
      width: "300px", // ← Fixed width (adjust if needed: 280–340px)
      maxWidth: "300px", // ← Critical: prevents growing wider
      minWidth: "260px",
      zIndex: 999999,
    }),
    menuList: (base) => ({
      ...base,
      maxHeight: "380px", // Plenty of vertical space
      padding: "8px",
      overflowY: "auto",
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isFocused ? "#4b5563" : "#374151",
      color: state.isSelected ? "#f97316" : "#e5e7eb",
      padding: "10px 12px",
      cursor: "pointer",
      fontSize: "13.5px",
      whiteSpace: "normal", // Long text wraps instead of expanding width
      wordBreak: "break-word",
      lineHeight: "1.4",
      "&:hover": {
        backgroundColor: "#4b5563",
      },
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: "4px 8px",
      display: "flex",
      flexWrap: "wrap",
      gap: "6px",
      alignItems: "flex-start",
    }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: "#4b5563",
      borderRadius: "6px",
      margin: 0,
      fontSize: "13px",
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: "#e5e7eb",
      padding: "3px 8px",
      paddingRight: "6px",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      maxWidth: "150px", // Truncates very long selected labels
    }),
    multiValueRemove: (base) => ({
      ...base,
      color: "#9ca3af",
      cursor: "pointer",
      paddingLeft: "4px",
      paddingRight: "4px",
      borderRadius: "4px",
      ":hover": {
        backgroundColor: "#f97316",
        color: "white",
      },
    }),
  };

  // PERFORMANCE: Memoized applyFiltersToItems with useCallback
  const applyFiltersToItems = useCallback(
    (items) => {
      return items.filter((item) => {
        // Category filter (multi-select)
        if (
          filters.category.length > 0 &&
          !filters.category.includes(item.category)
        ) {
          return false;
        }

        // Section filter (multi-select)
        if (
          filters.section.length > 0 &&
          !filters.section.includes(item.sectionNumber)
        ) {
          return false;
        }

        // Bid Area filter (multi-select)
        if (
          filters.bidArea.length > 0 &&
          !filters.bidArea.includes(item.bidArea)
        ) {
          return false;
        }

        // Condition filter (multi-select) — match on normalized name
        if (filters.condition.length > 0) {
          const normalized = normalizeConditionName(item.conditionName);
          if (!filters.condition.includes(normalized)) {
            return false;
          }
        }

        // UOM filter (multi-select)
        if (filters.uom.length > 0) {
          const uom = item.category === "M" ? item.container : item.uom;
          if (!filters.uom.includes(uom)) return false;
        }

        // Size filter (multi-select)
        if (filters.size.length > 0 && !filters.size.includes(item.size)) {
          return false;
        }

        // Vendor filter (multi-select)
        if (filters.vendor.length > 0) {
          const vendorId =
            item.vendor && typeof item.vendor === "object"
              ? item.vendor._id
              : item.vendor;
          const hasNullFilter = filters.vendor.includes(null);
          const hasVendorMatch = filters.vendor.includes(vendorId);
          if (
            item.category !== "M" ||
            (!hasVendorMatch && !(hasNullFilter && !vendorId))
          ) {
            return false;
          }
        }

        // PO/WO filter (multi-select)
        if (filters.poWo.length > 0) {
          let matched = false;

          for (const selectedValue of filters.poWo) {
            const isPO = selectedValue.startsWith("PO-");
            const isWO = selectedValue.startsWith("WO-");
            const selectedNumber = selectedValue.slice(3).trim();

            if (isPO) {
              matched = (project.purchaseOrders || []).some((po) => {
                const poNumberStr = String(po.poNumber || "").trim();
                const poNumber = poNumberStr
                  .replace(/^(PO[-_\s]*)/i, "")
                  .trim();
                if (poNumber !== selectedNumber) return false;
                return (po.lineItems || []).some((li) => {
                  if (!li.budgetItem) return false;
                  const ref =
                    typeof li.budgetItem === "object"
                      ? li.budgetItem._id
                      : li.budgetItem;
                  return ref === item._id;
                });
              });
            } else if (isWO) {
              matched = (project.workOrders || []).some((wo) => {
                const woNumberStr = String(wo.woNumber || "").trim();
                const woNumber = woNumberStr
                  .replace(/^(WO[-_\s]*)/i, "")
                  .trim();
                if (woNumber !== selectedNumber) return false;
                return (wo.lineItems || []).some((li) => {
                  if (!li.budgetItem) return false;
                  const ref =
                    typeof li.budgetItem === "object"
                      ? li.budgetItem._id
                      : li.budgetItem;
                  return ref === item._id;
                });
              });
            }

            if (matched) break;
          }

          if (!matched) return false;
        }

        return true;
      });
    },
    [filters, project.purchaseOrders, project.workOrders],
  );

  const getDisplayQty = (item) => {
    if (!item) return 0;
    if (item.netRequiredQty !== undefined) return item.netRequiredQty;
    return item.category === "L"
      ? (item.quantity ?? 0)
      : (item.requiredQty ?? 0);
  };

  const getDisplayPrice = (item) => {
    if (!item) return 0;
    if (item.latestPrice !== undefined) return item.latestPrice;
    if (item.category === "L") return item.unitRate ?? item.price ?? 0;
    return item.price ?? item.cost ?? 0;
  };

  const ItemStatusBadges = ({ item }) => {
    const badges = [];

    if (item.isHistoricalPriceLine) {
      badges.push(
        <span
          key="historical"
          className="inline-flex items-center px-1.5 py-0.5 text-[9px] font-semibold rounded bg-gray-600/80 text-gray-200 border border-gray-500/50"
        >
          ORDERED @ OLD PRICE
        </span>,
      );
    } else if (item.isPriceChangeRow) {
      badges.push(
        <span
          key="newprice"
          className="inline-flex items-center px-1.5 py-0.5 text-[9px] font-semibold rounded bg-purple-600/80 text-purple-100 border border-purple-400/50"
        >
          NEW PRICE
        </span>,
      );
    }

    if (item.isNewSpec) {
      badges.push(
        <span
          key="sizechange"
          className="inline-flex items-center px-1.5 py-0.5 text-[9px] font-semibold rounded bg-blue-600/80 text-blue-100 border border-blue-400/50"
        >
          SIZE CHANGE
        </span>,
      );
    }

    if (item.closedOutBySizeChange) {
      badges.push(
        <span
          key="closed"
          className="inline-flex items-center px-1.5 py-0.5 text-[9px] font-semibold rounded bg-yellow-600/80 text-yellow-100 border border-yellow-400/50"
        >
          CLOSED
        </span>,
      );
    }

    if (
      item.hasVariance &&
      !item.isHistoricalPriceLine &&
      !item.isPriceChangeRow &&
      !item.isNewSpec
    ) {
      badges.push(
        <span
          key="variance"
          className="inline-flex items-center px-1.5 py-0.5 text-[9px] font-semibold rounded bg-amber-600/80 text-amber-100 border border-amber-400/50"
        >
          VARIANCE
        </span>,
      );
    }

    if (badges.length === 0) return null;

    return <div className="flex gap-1 flex-wrap mt-0.5">{badges}</div>;
  };

  const calcItemAmount = (item, options = {}) => {
    if (!item) return 0;

    const { useOriginalQty = false } = options;

    // Determine quantity based on category and whether we want original or current
    let qty;
    if (item.category === "M") {
      if (useOriginalQty && item.originalRequiredQty != null) {
        qty = item.originalRequiredQty;
      } else if (useOriginalQty && item.originalQty != null) {
        qty = item.originalQty;
      } else {
        qty = item.requiredQty ?? item.quantity ?? 0;
      }
    } else {
      // Labor
      if (useOriginalQty && item.originalQty != null) {
        qty = item.originalQty;
      } else if (useOriginalQty && item.originalRequiredQty != null) {
        qty = item.originalRequiredQty;
      } else {
        qty = item.quantity ?? 0;
      }
    }

    // Determine rate based on category
    const rate =
      item.category === "M"
        ? (item.cost ?? item.price ?? item.unitRate ?? 0)
        : (item.unitRate ?? item.price ?? 0);

    // Calculate coverage for area/linear units
    let coverage = 1;
    if (item.category === "M") {
      const uom = (item.container || item.uom || "").toLowerCase();
      const isAreaBased = ["sf", "square", "sq ft", "sq.ft"].some((t) =>
        uom.includes(t),
      );
      const isLinear = ["lf", "linear", "lin ft", "lin.ft"].some((t) =>
        uom.includes(t),
      );

      if (isAreaBased || isLinear) {
        coverage =
          Number(parseFloat(item.coveragePerUnit)) ||
          calculateCoverage(item.size, item.uom || item.container) ||
          1;
      }
    }

    return Number((qty * coverage * rate).toFixed(2)) || 0;
  };

  const calculateBreakdown = useMemo(() => {
    if (summaryTotals.isLoading) {
      return {
        totalEstimatedCost: 0,
        originalEstimatedCost: 0,
        originalBaseCost: 0,
        changeOrderImpact: 0,
        tAndMCost: 0,
        varianceImpact: 0,
        formattedTotal: "Loading...",
        formattedOriginal: "...",
        formattedOriginalBase: "...",
        formattedChangeOrder: "...",
        formattedTAndM: "...",
        formattedVariance: "...",
      };
    }

    const fmt = (n) =>
      n.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

    return {
      ...summaryTotals,
      formattedTotal: fmt(summaryTotals.totalEstimatedCost),
      formattedOriginal: fmt(summaryTotals.originalEstimatedCost),
      formattedOriginalBase: fmt(summaryTotals.originalBaseCost),
      formattedChangeOrder: fmt(summaryTotals.changeOrderImpact),
      formattedTAndM: fmt(summaryTotals.tAndMCost),
      formattedVariance: fmt(summaryTotals.varianceImpact),
    };
  }, [summaryTotals]);

  // Update the destructuring:
  const {
    formattedTotal,
    formattedOriginalBase: originalBaseCostFormatted, // NEW: raw base
    formattedOriginal: originalEstimatedCost, // NET with deltas
    formattedChangeOrder: changeOrderImpact,
    formattedTAndM: tAndMCost,
    varianceImpact: rawVarianceImpact,
  } = calculateBreakdown;
  // =====================================================
  // HELPER: Calculate budget total from line items (for single budget view)
  const getBudgetDisplayTotal = useCallback(
    (budgetId) => {
      const budgetIdStr = budgetId.toString();

      // Check cache first (populated by project-summary and budget-detail)
      if (budgetTotalsCache[budgetIdStr] !== undefined) {
        return budgetTotalsCache[budgetIdStr];
      }

      // Fallback for loading state
      const budget = budgets.find((b) => b._id === budgetId);
      if (!budget) return 0;

      // For variance budgets, use grandTotal as-is (they represent deltas)
      if (budget.isVariance) {
        return budget.grandTotal || 0;
      }

      // For original/CO budgets, calculate from line items
      return (budget.lineItems || [])
        .filter((item) => !item.isNewSpec && !item.isChangeDelta)
        .reduce((sum, item) => sum + calcItemAmount(item), 0);
    },
    [budgetTotalsCache, budgets],
  );

  const currentBudgetRawItems = useMemo(() => {
    const sortItems = (items) => {
      return [...items].sort((a, b) => {
        const assA = (a.conditionName || "").trim().toLowerCase();
        const assB = (b.conditionName || "").trim().toLowerCase();
        if (assA !== assB) return assA.localeCompare(assB);

        const secA = a.sectionNumber || "";
        const secB = b.sectionNumber || "";
        if (secA !== secB) return secA.localeCompare(secB);

        if (a.isHistoricalPriceLine && !b.isHistoricalPriceLine) return -1;
        if (!a.isHistoricalPriceLine && b.isHistoricalPriceLine) return 1;

        return (a.itemNumber || 0) - (b.itemNumber || 0);
      });
    };

    // SINGLE BUDGET VIEW: Use budgetDetailItems
    if (!expandedBudget) return [];

    const budget = project?.budgets?.find((b) => b._id === expandedBudget);
    if (!budget) return [];

    // If editing a non-confirmed budget, use editedItems
    if (editingBudget === expandedBudget && budget.status !== "confirmed") {
      return editedItems;
    }

    // Use budgetDetailItems from the API
    if (budgetDetailItems.length > 0) {
      return sortItems(budgetDetailItems);
    }

    // Loading state - return empty or show skeleton
    return [];
  }, [
    expandedBudget,
    project?.budgets,
    editingBudget,
    editedItems,
    budgetDetailItems,
  ]);
  // ─────────────────────────────────────────────────────────────────────────────
  //  HELPER FUNCTIONS FOR CLEAN PRICE / QTY DISPLAY (Option 1)
  // ─────────────────────────────────────────────────────────────────────────────

  // PERFORMANCE: Memoized getFilterOptions with useCallback
  const getFilterOptions = useCallback(
    (field) => {
      const rawItems = currentBudgetRawItems;
      if (!rawItems.length) return [];

      const uniqueValues = new Set();
      rawItems.forEach((item) => {
        let value;
        switch (field) {
          case "category":
            value = item.category;
            break;
          case "section":
          case "sectionNumber":
            value = item.sectionNumber;
            break;
          case "bidArea":
            value = item.bidArea;
            break;
          case "condition":
            value = item.conditionName;
            break;
          case "uom":
            value =
              item.category === "M" ? getDisplayUom(item) : item.uom || "—";
            break;
          case "size":
            value = item.size;
            break;
          case "vendor":
            if (item.category === "M" && item.vendor) {
              value =
                typeof item.vendor === "object" ? item.vendor._id : item.vendor;
            }
            break;
          default:
            value = item[field];
        }
        if (value) uniqueValues.add(value);
      });

      if (field === "vendor") {
        return [
          { value: null, label: "Unassigned" },
          ...Array.from(uniqueValues).map((id) => ({
            value: id,
            label: vendors.find((v) => v._id === id)?.companyName || "Unknown",
          })),
        ];
      }
      if (field === "condition") {
        // Group by normalized name so "1 - MS.12a" and "159 - MS.12a" collapse into "MS.12a"
        const normalizedSet = new Set();
        rawItems.forEach((item) => {
          if (item.conditionName) {
            normalizedSet.add(normalizeConditionName(item.conditionName));
          }
        });

        const values = Array.from(normalizedSet);

        const allNumericLike = values.every((v) => {
          const str = String(v || "").trim();
          return str === "" || !isNaN(parseFloat(str)) || /^\d/.test(str);
        });

        if (allNumericLike) {
          values.sort((a, b) => {
            const aNum = parseFloat(String(a || "").trim());
            const bNum = parseFloat(String(b || "").trim());
            if (!isNaN(aNum) && !isNaN(bNum)) return aNum - bNum;
            return String(a || "").localeCompare(String(b || ""));
          });
        } else {
          values.sort((a, b) => String(a || "").localeCompare(String(b || "")));
        }

        return values.map((v) => ({
          value: v,
          label: v || "N/A",
        }));
      }

      if (field === "poWo") {
        const poSet = new Set();
        const woSet = new Set();
        project.purchaseOrders?.forEach((po) => {
          if (po.poNumber) poSet.add(po.poNumber);
        });
        project.workOrders?.forEach((wo) => {
          if (wo.woNumber) woSet.add(wo.woNumber);
        });
        const poOptions = Array.from(poSet).map((n) => ({
          value: `PO-${n}`,
          label: `PO-${n}`,
        }));
        const woOptions = Array.from(woSet).map((n) => ({
          value: `WO-${n}`,
          label: `WO-${n}`,
        }));
        return [...poOptions, ...woOptions].sort((a, b) =>
          a.label.localeCompare(b.label),
        );
      }

      return Array.from(uniqueValues)
        .sort()
        .map((v) => ({ value: v, label: v || "N/A" }));
    },
    [
      currentBudgetRawItems,
      vendors,
      project?.purchaseOrders,
      project?.workOrders,
    ],
  );

  // PERFORMANCE: Memoized filtered budget items
  const filteredBudgetItems = useMemo(() => {
    const columnFiltered = applyFiltersToItems(currentBudgetRawItems);
    if (!filters.descriptionSearch) return columnFiltered;
    return multiTokenSearchItems(columnFiltered, filters.descriptionSearch);
  }, [currentBudgetRawItems, applyFiltersToItems, filters.descriptionSearch]);

  // PERFORMANCE: Memoized paginated items (only render 100 at a time)
  const paginatedItems = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredBudgetItems.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredBudgetItems, currentPage]);

  const totalPages = Math.ceil(filteredBudgetItems.length / ITEMS_PER_PAGE);

  // PERFORMANCE: Memoized totals calculation
  const filteredTotals = useMemo(() => {
    return filteredBudgetItems.reduce(
      (acc, item) => {
        const amount = calcItemAmount(item); // ← now always correct!
        return {
          total: acc.total + amount,
          materials: acc.materials + (item.category === "M" ? amount : 0),
          labor: acc.labor + (item.category === "L" ? amount : 0),
          orderedQty: acc.orderedQty + (item.qtyOrdered || 0),
          pendingQty: acc.pendingQty + (item.qtyPending || 0),
          remainingQty:
            acc.remainingQty +
            (item.isHistoricalPriceLine ? 0 : item.netRemaining || 0),
          itemsWithVariance: acc.itemsWithVariance + (item.hasVariance ? 1 : 0),
        };
      },
      {
        total: 0,
        materials: 0,
        labor: 0,
        orderedQty: 0,
        pendingQty: 0,
        remainingQty: 0,
        itemsWithVariance: 0,
      },
    );
  }, [filteredBudgetItems]);

  // Near the top of your component, add this memoized helper
  // NEW — uses changeOrderBreakdown from API data
  const getVarianceHistoryForItem = useCallback(
    (item) => {
      if (!item) return [];

      // Primary source: changeOrderBreakdown array from the API
      if (item.changeOrderBreakdown && item.changeOrderBreakdown.length > 0) {
        return item.changeOrderBreakdown;
      }

      // Secondary source: varianceBreakdown array
      if (item.varianceBreakdown && item.varianceBreakdown.length > 0) {
        return item.varianceBreakdown;
      }

      // Fallback: search budget.lineItems (legacy path for non-API data)
      if (expandedBudget) {
        const currentBudget = project.budgets.find(
          (b) => b._id === expandedBudget,
        );
        if (currentBudget) {
          const itemId = item._id || item.key;
          return (currentBudget.lineItems || []).filter(
            (vItem) =>
              vItem.originalLineItemId === itemId &&
              !vItem.isOriginal &&
              !vItem.isNewSpec &&
              vItem.changeType !== "size_change_closeout",
          );
        }
      }

      return [];
    },
    [expandedBudget, project.budgets],
  );

  // Select all filtered items
  // Select all filtered items
  const handleSelectAllFiltered = useCallback(() => {
    const allSelected = filteredBudgetItems.every((item) =>
      selectedLineItems.some(
        (s) => getItemSelectionKey(s) === getItemSelectionKey(item),
      ),
    );

    if (allSelected) {
      const allFilteredKeys = new Set(
        filteredBudgetItems.map((i) => getItemSelectionKey(i)),
      );
      setSelectedLineItems((prev) =>
        prev.filter((i) => !allFilteredKeys.has(getItemSelectionKey(i))),
      );
    } else {
      setSelectedLineItems((prev) => {
        const existingKeys = new Set(prev.map((i) => getItemSelectionKey(i)));
        const newItems = filteredBudgetItems.filter(
          (i) => !existingKeys.has(getItemSelectionKey(i)),
        );
        return [...prev, ...newItems];
      });
    }
  }, [filteredBudgetItems, selectedLineItems, getItemSelectionKey]);

  const allFilteredSelected = useMemo(() => {
    if (filteredBudgetItems.length === 0) return false;
    return filteredBudgetItems.every((item) =>
      selectedLineItems.some(
        (s) => getItemSelectionKey(s) === getItemSelectionKey(item),
      ),
    );
  }, [filteredBudgetItems, selectedLineItems, getItemSelectionKey]);

  const isUploadEnabled = budgetTitle.trim() !== "" && budgetFile !== null;

  const toggleBudgetItems = (budgetId) => {
    if (expandedBudget === budgetId) {
      setExpandedBudget(null);
      if (editingBudget === budgetId) setEditingBudget(null);
      setSelectedLineItems([]);
      clearAllFilters();
    } else {
      setExpandedBudget(budgetId);
      const mainBudget = budgets.find((b) => b._id === budgetId);
      if (
        mainBudget &&
        mainBudget.status !== "confirmed" &&
        !mainBudget.isVariance
      ) {
        handleEditBudget(budgetId);
      }
      setSelectedLineItems([]);
      clearAllFilters();
    }
  };

  const formatNumber = (num) =>
    (num ?? 0).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const renderVarianceSummary = (item) => {
    const breakdownEntries = getVarianceHistoryForItem(item);
    if (!breakdownEntries || breakdownEntries.length === 0) {
      // No variance data — show a simple "no changes" message if item has variance flag
      if (item.hasVariance) {
        return (
          <tr className="bg-gray-900/70 border-t border-amber-900/30">
            <td colSpan={15} className="p-4">
              <p className="text-sm text-gray-500 italic">
                Variance applied but no detailed breakdown available.
              </p>
            </td>
          </tr>
        );
      }
      return null;
    }

    // Check if entries are from API (have budgetId) or legacy (have _id)
    const isAPIFormat = breakdownEntries[0]?.budgetId !== undefined;

    if (isAPIFormat) {
      // ── API FORMAT: Group by budgetId ──
      const groups = {};
      breakdownEntries.forEach((entry) => {
        const budgetIdStr = entry.budgetId?.toString() || "unknown";
        if (!groups[budgetIdStr]) {
          groups[budgetIdStr] = {
            budgetId: entry.budgetId,
            budgetTitle: entry.budgetTitle || "Variance",
            budgetType: entry.budgetType || "variance",
            reason: entry.reason || "",
            date: entry.date,
            entries: [],
          };
        }
        groups[budgetIdStr].entries.push(entry);
      });

      return (
        <tr className="bg-gray-900/70 border-t border-amber-900/30">
          <td colSpan={15} className="p-4">
            <div className="space-y-3">
              <h6 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
                Change & Variance Log
              </h6>

              {Object.values(groups).map((group, gIdx) => {
                const totalDeltaQty = group.entries.reduce(
                  (sum, e) => sum + (e.deltaQty || 0),
                  0,
                );
                const totalDeltaAmount = group.entries.reduce(
                  (sum, e) => sum + (e.deltaAmount || 0),
                  0,
                );

                return (
                  <div
                    key={group.budgetId || gIdx}
                    className={`p-3 rounded-lg border ${
                      totalDeltaAmount > 0
                        ? "border-red-800/40 bg-red-950/20"
                        : totalDeltaAmount < 0
                          ? "border-emerald-800/40 bg-emerald-950/20"
                          : "border-gray-700 bg-gray-800/50"
                    }`}
                  >
                    {/* Header */}
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-2">
                      <div className="flex items-center gap-2">
                        <div
                          className={`w-2 h-2 rounded-full ${
                            totalDeltaAmount > 0
                              ? "bg-red-500"
                              : totalDeltaAmount < 0
                                ? "bg-emerald-500"
                                : "bg-amber-500"
                          }`}
                        />
                        <span
                          className={`px-1.5 py-0.5 text-[9px] font-semibold rounded ${
                            group.budgetType === "change_order"
                              ? "bg-purple-600/80 text-purple-100"
                              : "bg-amber-600/80 text-amber-100"
                          }`}
                        >
                          {group.budgetType === "change_order"
                            ? "CHANGE ORDER"
                            : "VARIANCE"}
                        </span>
                        <h6 className="text-sm font-semibold text-white">
                          {group.budgetTitle}
                        </h6>
                      </div>

                      <div className="flex items-center gap-3 text-sm font-medium">
                        {totalDeltaQty !== 0 && (
                          <span
                            className={
                              totalDeltaQty > 0
                                ? "text-red-400"
                                : "text-emerald-400"
                            }
                          >
                            Qty: {totalDeltaQty > 0 ? "+" : ""}
                            {totalDeltaQty}
                          </span>
                        )}
                        {totalDeltaAmount !== 0 && (
                          <span
                            className={
                              totalDeltaAmount > 0
                                ? "text-red-400"
                                : "text-emerald-400"
                            }
                          >
                            {totalDeltaAmount > 0 ? "+" : ""}$
                            {Math.abs(totalDeltaAmount).toLocaleString(
                              "en-US",
                              {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              },
                            )}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Reason */}
                    {group.reason && (
                      <div className="mb-2 pl-3 border-l-2 border-amber-600/70 bg-amber-950/20 p-2 rounded-r">
                        <p className="text-xs text-amber-100">
                          <span className="font-medium text-amber-300">
                            Reason:{" "}
                          </span>
                          {group.reason}
                        </p>
                      </div>
                    )}

                    {/* Individual delta entries */}
                    <div className="space-y-1 mt-2">
                      {group.entries.map((entry, eIdx) => {
                        const changeLabel =
                          entry.changeType === "price_change_new"
                            ? "Price Change"
                            : entry.changeType === "size_change_new"
                              ? "Size Change (New)"
                              : entry.changeType === "size_change_closeout"
                                ? "Size Closeout"
                                : entry.changeType === "qty_reduction"
                                  ? "Qty Reduction"
                                  : entry.changeType === "qty_increase"
                                    ? "Qty Increase"
                                    : entry.changeType || "Qty Change";

                        return (
                          <div
                            key={entry.deltaItemId || eIdx}
                            className="flex items-center justify-between text-xs bg-gray-800/60 rounded px-2 py-1.5"
                          >
                            <div className="flex items-center gap-3">
                              <span className="text-gray-500 font-mono w-6">
                                {entry.category || item.category}
                              </span>
                              <span className="text-gray-300 font-medium">
                                {changeLabel}
                              </span>
                              {entry.isNewSpec && (
                                <span className="px-1 py-0.5 text-[8px] font-semibold bg-blue-600/60 text-blue-200 rounded">
                                  NEW SPEC
                                </span>
                              )}
                              {entry.sizeChangedFrom && (
                                <span className="text-gray-500">
                                  from {entry.sizeChangedFrom} → {entry.size}
                                </span>
                              )}
                              {entry.priceChangedFrom != null &&
                                entry.priceChangedTo != null && (
                                  <span className="text-gray-500">
                                    ${formatNumber(entry.priceChangedFrom)} → $
                                    {formatNumber(entry.priceChangedTo)}
                                  </span>
                                )}
                            </div>

                            <div className="flex items-center gap-4">
                              {entry.deltaQty !== 0 && (
                                <span
                                  className={`font-mono ${
                                    entry.deltaQty > 0
                                      ? "text-red-400"
                                      : "text-emerald-400"
                                  }`}
                                >
                                  {entry.deltaQty > 0 ? "+" : ""}
                                  {entry.deltaQty}
                                </span>
                              )}
                              {entry.deltaAmount !== 0 && (
                                <span
                                  className={`font-mono ${
                                    entry.deltaAmount > 0
                                      ? "text-red-400"
                                      : "text-emerald-400"
                                  }`}
                                >
                                  {entry.deltaAmount > 0 ? "+" : ""}$
                                  {Math.abs(entry.deltaAmount).toLocaleString(
                                    "en-US",
                                    {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                    },
                                  )}
                                </span>
                              )}
                              {entry.date && (
                                <span className="text-gray-500 text-[10px]">
                                  {new Date(entry.date).toLocaleDateString()}
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </td>
        </tr>
      );
    }

    // ── LEGACY FORMAT: fallback for budget.lineItems data ──
    const varianceGroups = breakdownEntries.reduce((acc, vItem) => {
      const varianceId = vItem.budgetId || vItem.varianceId;
      if (!acc[varianceId]) {
        acc[varianceId] = {
          variance: project.budgets.find((b) => b._id === varianceId) || {},
          items: [],
        };
      }
      acc[varianceId].items.push(vItem);
      return acc;
    }, {});

    return (
      <tr className="bg-gray-900/70 border-t border-amber-900/30">
        <td colSpan={15} className="p-4">
          <div className="space-y-4">
            {Object.values(varianceGroups).map(({ variance, items }) => {
              const totalDeltaQty = items.reduce(
                (sum, v) => sum + (v.requiredQty || v.quantity || 0),
                0,
              );

              return (
                <div
                  key={variance._id}
                  className={`p-4 rounded-lg border ${
                    variance.grandTotal > 0
                      ? "border-red-800/40 bg-red-950/20"
                      : variance.grandTotal < 0
                        ? "border-emerald-800/40 bg-emerald-950/20"
                        : "border-gray-700 bg-gray-800/50"
                  }`}
                >
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-2.5 h-2.5 rounded-full ${
                          variance.grandTotal > 0
                            ? "bg-red-500"
                            : variance.grandTotal < 0
                              ? "bg-emerald-500"
                              : "bg-amber-500"
                        }`}
                      />
                      <h6 className="text-sm font-semibold text-white">
                        {variance.title || "Variance Adjustment"}
                      </h6>
                      {variance.status === "confirmed" && (
                        <span className="px-2 py-0.5 text-[10px] font-medium bg-green-900/70 text-green-300 rounded-full">
                          Confirmed
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-4 text-sm font-medium">
                      {variance.grandTotal !== 0 && (
                        <span
                          className={
                            variance.grandTotal > 0
                              ? "text-red-400"
                              : "text-emerald-400"
                          }
                        >
                          {variance.grandTotal > 0 ? "+" : ""}$
                          {Math.abs(variance.grandTotal).toLocaleString(
                            "en-US",
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            },
                          )}
                        </span>
                      )}
                      {totalDeltaQty !== 0 && (
                        <span
                          className={
                            totalDeltaQty > 0
                              ? "text-red-400"
                              : "text-emerald-400"
                          }
                        >
                          Qty: {totalDeltaQty > 0 ? "+" : ""}
                          {totalDeltaQty}
                        </span>
                      )}
                    </div>
                  </div>

                  {variance.varianceReason && (
                    <div className="mb-3 pl-3 border-l-3 border-amber-600/70 bg-amber-950/20 p-2.5 rounded-r">
                      <p className="text-sm text-amber-100">
                        <span className="font-medium text-amber-300">
                          Reason:{" "}
                        </span>
                        {variance.varianceReason}
                      </p>
                    </div>
                  )}

                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-xs text-gray-300">
                    <div>
                      <span className="text-gray-500 block mb-0.5">
                        Materials
                      </span>
                      <span
                        className={
                          variance.totalMaterialCost > 0
                            ? "text-red-400"
                            : "text-emerald-400"
                        }
                      >
                        $
                        {Math.abs(
                          variance.totalMaterialCost || 0,
                        ).toLocaleString("en-US", {
                          maximumFractionDigits: 0,
                        })}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500 block mb-0.5">Labor</span>
                      <span
                        className={
                          variance.totalLaborCost > 0
                            ? "text-red-400"
                            : "text-emerald-400"
                        }
                      >
                        $
                        {Math.abs(variance.totalLaborCost || 0).toLocaleString(
                          "en-US",
                          { maximumFractionDigits: 0 },
                        )}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500 block mb-0.5">Date</span>
                      <span>
                        {new Date(
                          variance.confirmedAt || variance.createdAt,
                        ).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div className="space-y-6">
      {isMounted &&
        dialog.open &&
        createPortal(
          <AlertDialog
            type={dialog.type}
            title={dialog.title}
            message={dialog.message}
            confirmText={dialog.confirmText || "OK"}
            cancelText={dialog.cancelText || "Close"}
            open={dialog.open}
            onClose={
              dialog.onClose ||
              (() => setDialog((prev) => ({ ...prev, open: false })))
            }
            onConfirm={dialog.onConfirm}
          />,
          document.body,
        )}
      {showVarianceModal && (
        <VarianceModal
          budgetId={expandedBudget}
          lineItems={selectedLineItems}
          vendors={vendors}
          bidAreaOptions={getFilterOptions("bidArea")}
          projectId={project._id}
          varianceId={isAddingToVariance ? selectedVarianceId : null}
          isAddingToVariance={isAddingToVariance}
          onClose={() => {
            setShowVarianceModal(false);
            setSelectedLineItems([]);
            setIsAddingToVariance(false);
            setSelectedVarianceId(null);
          }}
          onVarianceCreated={(updatedProject) => {
            setProject(updatedProject);
            setShowVarianceModal(false);
            setSelectedLineItems([]);
            setIsAddingToVariance(false);
            setSelectedVarianceId(null);
          }}
          setProject={setProject}
          setDialog={setDialog}
        />
      )}
      <div className="bg-gradient-to-br from-gray-800 to-gray-800/80 p-6 rounded-xl shadow-lg border border-gray-700/50 mb-6">
        {/* Header Row */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-500/10 rounded-lg">
              <svg
                className="w-5 h-5 text-orange-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">
                Budget Management
              </h3>
              <p className="text-xs text-gray-500">
                Budget overview and tracking
              </p>
            </div>
          </div>

          {/* View Mode Toggle */}
          <div className="flex items-center gap-4 bg-gray-900/50 px-4 py-2 rounded-xl border border-gray-700/50">
            <button
              onClick={() => setViewMode("single")}
              className={`text-sm font-medium transition-colors duration-200 ${
                viewMode === "single"
                  ? "text-white"
                  : "text-gray-500 hover:text-gray-300"
              }`}
            >
              Budget
            </button>
            <div className="w-px h-4 bg-gray-600" />
            <button
              onClick={() => setViewMode("assembly")}
              className={`text-sm font-medium transition-colors duration-200 ${
                viewMode === "assembly"
                  ? "text-white"
                  : "text-gray-500 hover:text-gray-300"
              }`}
            >
              Assembly
            </button>
          </div>
        </div>

        {/* Metrics Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-5 gap-4">
          {/* Total Estimated - Primary/Highlighted */}
          <div className="bg-gradient-to-br from-orange-500/10 to-orange-600/5 p-4 rounded-xl border border-orange-500/20">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-1.5 h-1.5 rounded-full bg-orange-400"></div>
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">
                Total Estimated
              </p>
            </div>
            <p className="text-2xl font-bold text-white">${formattedTotal}</p>
          </div>
          {/* Original Estimated */}

          {/* Original Base Cost — what we started with */}
          <div className="bg-gray-900/40 p-4 rounded-xl border border-gray-700/50 hover:border-gray-600/50 transition-colors">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-1.5 h-1.5 rounded-full bg-gray-500"></div>
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">
                Original Est.
              </p>
            </div>
            <p className="text-xl font-semibold text-gray-200">
              ${originalBaseCostFormatted}
            </p>
          </div>
          {/* T&M Cost */}
          <div className="bg-gray-900/40 p-4 rounded-xl border border-gray-700/50 hover:border-gray-600/50 transition-colors">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-1.5 h-1.5 rounded-full bg-blue-400"></div>
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">
                T&M Cost
              </p>
            </div>
            <p className="text-xl font-semibold text-gray-200">${tAndMCost}</p>
          </div>
          {/* Change Order Impact */}
          <div className="bg-gray-900/40 p-4 rounded-xl border border-gray-700/50 hover:border-gray-600/50 transition-colors">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-1.5 h-1.5 rounded-full bg-purple-400"></div>
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">
                Change Orders
              </p>
            </div>
            <p className="text-xl font-semibold text-gray-200">
              ${changeOrderImpact}
            </p>
          </div>
          {/* Variance Impact - Conditional Styling */}
          <div
            className={`p-4 rounded-xl border transition-colors ${
              rawVarianceImpact > 0
                ? "bg-red-500/10 border-red-500/30"
                : rawVarianceImpact < 0
                  ? "bg-green-500/10 border-green-500/30"
                  : "bg-gray-900/40 border-gray-700/50"
            }`}
          >
            <div className="flex items-center gap-2 mb-2">
              <div
                className={`w-1.5 h-1.5 rounded-full ${
                  rawVarianceImpact > 0
                    ? "bg-red-400"
                    : rawVarianceImpact < 0
                      ? "bg-green-400"
                      : "bg-gray-500"
                }`}
              ></div>
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">
                Variance Impact
                {rawVarianceImpact > 0 && (
                  <span className="ml-1 text-red-400 text-xs">(Overrun)</span>
                )}
                {rawVarianceImpact < 0 && (
                  <span className="ml-1 text-green-400 text-xs">(Savings)</span>
                )}
              </p>
            </div>

            <div className="flex items-center gap-2">
              {rawVarianceImpact !== 0 && (
                <svg
                  className={`w-5 h-5 flex-shrink-0 ${
                    rawVarianceImpact > 0 ? "text-red-400" : "text-green-400"
                  } ${rawVarianceImpact < 0 ? "rotate-180" : ""}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 10l7-7m0 0l7 7m-7-7v18"
                  />
                </svg>
              )}

              <p
                className={`text-2xl font-bold ${
                  rawVarianceImpact > 0
                    ? "text-red-400"
                    : rawVarianceImpact < 0
                      ? "text-green-400"
                      : "text-gray-200"
                }`}
              >
                {rawVarianceImpact > 0 && "+"}$
                {Math.abs(rawVarianceImpact).toLocaleString("en-US", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </p>
            </div>
          </div>
        </div>
      </div>
      {project.budgets?.length === 0 && (
        <div className="flex space-x-4 mb-4">
          <button
            onClick={() => setShowBudgetForm(true)}
            className="px-4 py-2 text-sm font-medium text-white bg-orange-400 rounded-md hover:bg-orange-500 transition-colors duration-200"
          >
            Create New Budget
          </button>
        </div>
      )}
      {project.budgets?.length === 0 && showBudgetForm && (
        <div className="space-y-4 bg-gray-700 shadow-lg rounded-lg p-6">
          <input
            type="text"
            value={budgetTitle}
            onChange={(e) => setBudgetTitle(e.target.value)}
            placeholder="Budget Title"
            className="block w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
          />
          <input
            type="file"
            accept=".csv, .xlsx"
            onChange={(e) => setBudgetFile(e.target.files[0])}
            className="block w-full text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-gray-600 file:text-gray-200 hover:file:bg-gray-500"
          />
          <p className="text-xs text-gray-400">
            Supported formats: CSV, XLSX. New format with "Condition" column is
            auto-detected.
          </p>
          <button
            onClick={handleFileUpload}
            disabled={!isUploadEnabled || isUploading}
            className={`px-4 py-2 text-sm font-medium text-white rounded-md transition-colors duration-200 ${
              isUploadEnabled && !isUploading
                ? "bg-orange-400 hover:bg-orange-500"
                : "bg-gray-600 cursor-not-allowed"
            }`}
          >
            {isUploading ? "Uploading..." : "Upload Budget"}
          </button>
        </div>
      )}

      <div>
        {budgets.length === 0 ? (
          <p className="text-gray-400 text-sm">No budgets available.</p>
        ) : viewMode === "assembly" ? (
          <AssemblyBudgetView
            allItems={assemblyViewItems}
            loading={loadingAssemblyView}
            vendors={vendors}
            project={project}
            selectedLineItems={selectedLineItems}
            onSelectLineItem={handleSelectLineItem}
            onSelectAllFiltered={handleSelectAllFiltered}
            getItemSelectionKey={getItemSelectionKey}
            onCreateVariances={handleCreateVariances}
          />
        ) : (
          renderSingleBudgetView()
        )}
      </div>
    </div>
  );
}
